import ReadWriteLock from "rwlock";
import { convertMarkdownToHtml } from "@common/markdown/markdownToHtml";
import { PackageBinaryParts } from "@common/utils/redline/ooxml/package-binary-parts";
import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import { inlineMermaidDiagrams } from "@common/markdown/mermaidToImage";
import { CANNOT_LOCATE_TARGET_MESSAGE, UserFacingError, WORD_BUSY_MESSAGE, isWordBusyRefusal, toError, userMessageOf } from "./errors";
import { retryWhileWordIsBusy } from "./word-busy-retry";
import { logWarning, reportError, reportMessage } from "./sentry-reporter";
import { toastError } from "./toast";
import { getApiKey } from "./addin-auth";
import { repairMergeError } from "./api";
import { OoxmlMergeError, mergeOoxmlPackages, redlineCarriesCommentThreads, restoreLiveResolveState } from "./ooxml-merge";
import { findUnresolvedPackageReferences, formatUnresolvedPackageReferences } from "./ooxml-preflight";
import {
    RecoveryOutcome,
    describeFirstDivergence,
    extractBodyContent,
    redlineSignature,
    redlinesDropped,
    verifyRecovery,
} from "./recovery-verification";
import { applyCommentChange, commentTarget, commentTargets, rangeAlreadyCarriesComment, type CommentRelocation, type CommentTarget, type CommentTargetRequest } from "./comment-anchors";
import { isWordCommentInsertRefusal, reportCommentInsertRefusalsOncePerOutcome, retryRefusedCommentInsert } from "./comment-insert-refusal";
import type { CommentPlacement } from "@common/types/issue";
import { APPLY_FAILURE_INSERT_FAILED, APPLY_FAILURE_LOCATION_NOT_FOUND, applyFailureReason, type ApplyFailureReason } from "@common/constants/appliedActions";
import { preserveParagraphBookmarks } from "./paragraph-bookmark-transplant";
import { entryPayloadChars, maxBatchPayloadChars, packMergeEntriesIntoBatches } from "./bulk-apply-batching";
import { type BulkMergeWriteSummary, NO_MERGE_WRITE } from "./bulk-merge-write-summary";
import { readCurrentOoxmlsOfBatchEntries } from "./batch-current-ooxml";
import { BulkApplyBodyRead } from "./bulk-apply-body-read";
import type { BulkApplyMergeEntry } from "./bulk-apply-merge-entry";
import { guardUntrackedWrite, UntrackedWriteRefused } from "./untracked-write-guard";
import type { RedlineFailure } from "./redline-failure";
import { normalizeWordErrorFields } from "./redline-failure";
import { redlineFailureFingerprint } from "./redline-failure-sentry";
import type { RedlineAttemptRecorder } from "./redline-apply-recorder";
import { createRedlineAttemptRecorder, recordHowManyRedlinesTheGestureSelected } from "./redline-apply-recorder";
import type { RedlineApplyContext } from "./redline-snapshot";
import { captureRedlineSnapshot, startApplyAttempt } from "./redline-snapshot";
import { bulkApplyClockMs, recordBulkApplySpan, timedBulkApplySpan, timedBulkApplySpanSync } from "./bulk-apply-run-timeline";
import type {
    RedlineApplySource,
    RedlineAttemptOutcome,
    RedlineDropReason,
    RedlineApplyDropCount,
} from "./redline-apply-outcome";
import { readReviewIdOfApplySource } from "./redline-apply-outcome";
import { BodyReplacement } from "./anchor/body-replacement";
import { UNOBSERVED_DOCUMENT_READ, type DocumentReadObserver } from "./document-read-progress";
import { wordCallTracker } from "./word-responsiveness";
import { wordWriteLockHolds } from "./word-write-lock-holds";

export { applyCommentChange, rangeAlreadyCarriesComment };

const documentRWLock = new ReadWriteLock();

const runWordReadOnce = <T>(fn: (context: Word.RequestContext) => Promise<T>): Promise<T> =>
    new Promise<T>((resolve, reject) => {
        documentRWLock.readLock((release: () => void) => {
            const finishTrackedWordCall = wordCallTracker.beginCall();
            Word.run(fn)
                .then(resolve)
                .catch(reject)
                .finally(() => {
                    finishTrackedWordCall();
                    release();
                });
        });
    });

// Read operations - multiple can run concurrently
export const withWordContextRead = async <T>(fn: (context: Word.RequestContext) => Promise<T>): Promise<T> =>
    retryWhileWordIsBusy(
        () => runWordReadOnce(fn),
        (retryNumber) => logWarning("[word-busy] Word refused a read as busy; retrying", { retryNumber }),
    );

export const withOfficeContextRead = async <T>(fn: (context: Office.Context) => Promise<T>): Promise<T> => {
    return new Promise<T>((resolve, reject) => {
        documentRWLock.readLock((release: () => void) => {
            const finishTrackedWordCall = wordCallTracker.beginCall();
            try {
                const result = fn(Office.context);
                /**
                 * WHEN fn returned a Promise
                 *   CONSEQUENCE
                 *     Keep the read lock until that Promise settles, so later document work
                 *     cannot interleave before this read has really finished.
                 */
                if (result instanceof Promise) {
                    /**
                     * WHEN fn returned a non-Promise value
                     *   CONSEQUENCE
                     *     Resolve now and release the read lock in this same turn.
                     *     No callback wait is needed for synchronous readers.
                     */
                    result
                        .then(resolve)
                        .catch(reject)
                        .finally(() => {
                            finishTrackedWordCall();
                            release();
                        });
                } else {
                    resolve(result);
                    finishTrackedWordCall();
                    release();
                }
            } catch (error) {
                reject(error);
                finishTrackedWordCall();
                release();
            }
        });
    });
};

let pendingForegroundWork = 0;

export const pendingForegroundWorkCount = (): number => pendingForegroundWork;

// Write operations - exclusive access
export const withWordContextWrite = async <T>(fn: (context: Word.RequestContext) => Promise<T>): Promise<T> => {
    pendingForegroundWork += 1;
    return new Promise<T>((resolve, reject) => {
        documentRWLock.writeLock((release: () => void) => {
            const finishTrackedWordCall = wordCallTracker.beginCall();
            const endWriteLockHold = wordWriteLockHolds.beginHold();
            Word.run(fn)
                .then(resolve)
                .catch(reject)
                .finally(() => {
                    finishTrackedWordCall();
                    pendingForegroundWork -= 1;
                    endWriteLockHold();
                    release();
                });
        });
    });
};

type WordChangeTrackingMode = Word.ChangeTrackingMode | "Off" | "TrackAll" | "TrackMineOnly";
export const withTemporaryTrackingMode = async (context: Word.RequestContext, mode: WordChangeTrackingMode, fn: (originalTrackingMode: WordChangeTrackingMode) => Promise<any>) => {
    let originalTrackingMode: WordChangeTrackingMode | undefined;
    try {
        context.document.load("changeTrackingMode");
        await context.sync();

        originalTrackingMode = context.document.changeTrackingMode;

        /**
         * WHEN the requested tracking mode differs from the document's current mode
         *   CONSEQUENCE
         *     Switch modes before running fn.
         *     Skip the write when both modes already match.
         */
        if (mode !== originalTrackingMode) {
            context.document.changeTrackingMode = mode;
        }

        return await fn(originalTrackingMode);
    } finally {
    const shouldRestoreTrackingMode = (): boolean => {
        const originalTrackingModeWasRead = originalTrackingMode !== undefined;
        if (!originalTrackingModeWasRead) return false;
        return mode !== originalTrackingMode;
    };
        /**
         * WHEN we successfully read the original mode and changed it on entry
         *   CONSEQUENCE
         *     Restore that original mode on exit.
         *     If the read never completed, or no change was made, leave it alone.
         */
        if (shouldRestoreTrackingMode()) {
            context.document.changeTrackingMode = originalTrackingMode;
            await context.sync().catch(() => undefined);
        }
    }
};

export const fetchPropertyFromDocComments = (propertyName: string) => withWordContextRead(async (context) => {
    const docProperties = context.document.properties.load();
    await context.sync();

    const propertyMatch = docProperties.comments.split(/\s/).find((prop) => prop.startsWith(`${propertyName}=`));

    /**
     * WHEN no `name=value` token starts with this property name
     *   CONSEQUENCE
     *     Return null.
     */
    if (!propertyMatch) {
        /**
         * WHEN the `name=value` token was found
         *   CONSEQUENCE
         *     Return only the stored value part.
         */
        return null;
    } else {
        /**
         * EXAMPLE
         *   propertyMatch     = "review_id=abc123"
         *   split("=")        -> ["review_id", "abc123"]
         *   [1]               -> "abc123"
         */
        return propertyMatch.split("=")[1];
    }
});

export const fetchMultiplePropertyValuesFromDocComments = (propertyName: string) => withWordContextRead(async (context) => {
    const docProperties = context.document.properties.load();
    await context.sync();

    const propertyMatch = docProperties.comments.split(/\s/).find((prop) => prop.startsWith(`${propertyName}=`));

    /**
     * WHEN the multi-value property token is absent
     *   CONSEQUENCE
     *     Return an empty array.
     */
    if (!propertyMatch) {
        /**
         * WHEN the multi-value `name=value1,value2,...` token was found
         *   CONSEQUENCE
         *     Parse its value into individual items.
         */
        return [];
    } else {
        /**
         * EXAMPLE
         *   propertyMatch     = "bookmarks=_p1,_p2"
         *   split("=")        -> ["bookmarks", "_p1,_p2"]
         *   [1]               -> "_p1,_p2"
         */
        const value = propertyMatch.split("=")[1];

        /**
         * WHEN the stored value has no comma
         *   CONSEQUENCE
         *     Return a one-element array, not a bare string.
         */
        if (!value.includes(',')) {
            return [value.trim()];
        }

        const result = value.split(",").map(v => v.trim()).filter(v => v.length > 0);
        return result;
    }
});

export const appendPropertyToDocComments = (keyEqVal: string) => withWordContextWrite(async (context) => {
    const docProperties = context.document.properties.load();
    await context.sync();

    const [propertyName, propertyValue] = keyEqVal.split("=");
    const existingProperty = docProperties.comments.split(/\s/).find((prop) => prop.startsWith(`${propertyName}=`));

    /**
     * WHEN this property name already has a `name=value` token
     *   CONSEQUENCE
     *     Append onto that token's value instead of creating a second token with
     *     the same name.
     */
    if (existingProperty) {
        /**
         * EXAMPLE
         *   existingProperty  = "reviewers=alice,bob"
         *   split("=")        -> ["reviewers", "alice,bob"]
         *   [1]               -> "alice,bob"
         */
        const existingValue = existingProperty.split("=")[1];
        const newValue = `${existingValue},${propertyValue}`;
        /**
         * WHEN this property name is not present yet
         *   CONSEQUENCE
         *     Add a fresh `name=value` token to the comments field.
         */
        docProperties.comments = docProperties.comments.replace(existingProperty, `${propertyName}=${newValue}`);
    } else {
        docProperties.comments = docProperties.comments + " " + keyEqVal;
    }
});

const DEFAULT_DOC_NAME = "current_document.docx";

export const fetchDocName = () => withWordContextRead(async () => {
    /**
     * WHEN the Office host exposes no current document object
     *   CONSEQUENCE
     *     Throw immediately. There is nowhere to read a document name from.
     */
    if (!Office.context.document) {
        throw new Error("An error occurred while looking for the document name");
    }
    let fromUrl = Office.context.document.url?.split(/\/|\\/).pop()?.trim();
    /**
     * WHEN the document URL already yields a last path segment
     *   CONSEQUENCE
     *     Use that name and skip the async file-properties fallback.
     */
    if (fromUrl) {
        /**
         * WHEN the URL-derived name has no `.docx` suffix
         *   CONSEQUENCE
         *     Append `.docx` so new-document URLs match the filename form used
         *     elsewhere.
         */
        if (!fromUrl.endsWith(".docx")) {
            fromUrl = fromUrl + ".docx"; // New documents can be saved without the .docx extension in the url
        }

        return fromUrl;
    }

    return new Promise<string>((resolve) => {
        Office.context.document.getFilePropertiesAsync((result: Office.AsyncResult<Office.FileProperties>) => {
            /**
             * WHEN file-properties lookup failed
             *   CONSEQUENCE
             *     Fall back to the default filename.
             *
             * WHY
             *   Document naming is display metadata. It must not block the caller's main
             *   operation.
             */
            if (result.status !== Office.AsyncResultStatus.Succeeded) {
                // getFilePropertiesAsync fails with 5001 on third-party WOPI hosts
                // (OfficeDev/office-js#5711); the name is display metadata, so a
                // default must never veto the operation that asked for it.
                resolve(DEFAULT_DOC_NAME);
                return;
            }
            const last = result.value.url?.split(/\/|\\/).pop()?.trim();
            /**
             * WHEN file properties produced no final path segment
             *   CONSEQUENCE
             *     Fall back to the default filename.
             */
            if (!last) {
                resolve(DEFAULT_DOC_NAME);
                return;
            }
            // New documents can be saved without the .docx extension in the url
            resolve(last.endsWith(".docx") ? last : `${last}.docx`);
        });
    });
});

export const getBookmarkedSectionsHtml = (bookmarks: string[]) => {

    return withWordContextRead(async (context) => {
        const { groupedRanges, groupedBookmarks } = await groupBookmarkedRanges(bookmarks, context);
        const htmlResults = groupedRanges.map((range) => range.getHtml());

        await context.sync();

        const htmlValues = htmlResults.map((html) => {
            let htmlValue = html.value;
            htmlValue = htmlValue.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "");
            htmlValue = htmlValue.replace(/on\w+="[^"]*"/gi, "");
            htmlValue = htmlValue.replace(/<a\s+[^>]*class\s*=\s*["']?msocomanchor["']?[^>]*>[\s\S]*?<\/a>/gi, "");
            htmlValue = htmlValue.replace(/<hr[^>]*>[\s\S]*/i, "</div></body></html>");
            return htmlValue;
        });

        return { htmlValues, groupedBookmarks };
    });
}

export const groupBookmarkedRanges = async (bookmarks: string[], context: Word.RequestContext) => {
    const bookmarkRangePromises = bookmarks.map(async (bookmark) => {
        const firstParagraphRange = context.document.getBookmarkRangeOrNullObject(bookmark).paragraphs.getFirstOrNullObject().getRange("Start");
        const lastParagraphRange = context.document.getBookmarkRangeOrNullObject(bookmark).paragraphs.getLastOrNullObject().getRange("End");
        const bookmarkRange = firstParagraphRange.expandTo(lastParagraphRange);
        return bookmarkRange;
    });
    const allBookmarkRanges = await Promise.all(bookmarkRangePromises);

    await context.sync();

    const bookmarkRanges = allBookmarkRanges.filter((range) => !range.isNullObject);

    const groupedRanges: Word.Range[] = [];
    const groupedBookmarks: string[] = [];

    const OVERLAPPING_RELATIONS = new Set([
        Word.LocationRelation.adjacentBefore,
        Word.LocationRelation.adjacentAfter,
        Word.LocationRelation.overlapsAfter,
        Word.LocationRelation.overlapsBefore,
        Word.LocationRelation.inside,
        Word.LocationRelation.insideStart,
        Word.LocationRelation.insideEnd,
        Word.LocationRelation.contains,
        Word.LocationRelation.containsStart,
        Word.LocationRelation.containsEnd,
        Word.LocationRelation.equal,
    ]);

    for (let i = 0; i < bookmarkRanges.length; i++) {
        const bookmarkRange = bookmarkRanges[i];

        /**
         * WHEN no overlap group exists yet
         *   CONSEQUENCE
         *     Seed the first group with this bookmark range and its bookmark name.
         */
        if (groupedRanges.length === 0) {
            groupedRanges.push(bookmarkRange);
            groupedBookmarks.push(bookmarks[i]);
            /**
             * WHEN the first group was just seeded
             *   CONSEQUENCE
             *     Skip overlap comparison for this bookmark. Its group is already set.
             */
            continue;
        }

        const comparisons = groupedRanges.map((gr) => bookmarkRange.compareLocationWith(gr));
        await context.sync();

        const matchingIndices: number[] = [];
        for (let j = 0; j < comparisons.length; j++) {
            /**
             * WHEN this grouped range touches or overlaps the bookmark range
             *   CONSEQUENCE
             *     Record its index as a match for later merge logic.
             */
            if (OVERLAPPING_RELATIONS.has(comparisons[j].value)) {
                matchingIndices.push(j);
            }
        }

        /**
         * WHEN the bookmark is disjoint from every existing group
         *   CONSEQUENCE
         *     Start a new group.
         */
        if (matchingIndices.length === 0) {
            groupedRanges.push(bookmarkRange);
            /**
             * WHEN exactly one existing group matches
             *   CONSEQUENCE
             *     Expand that one group to include this bookmark.
             */
            groupedBookmarks.push(bookmarks[i]);
        } else if (matchingIndices.length === 1) {
            /**
             * WHEN the bookmark bridges several existing groups
             *   CONSEQUENCE
             *     Merge those groups into one combined range.
             */
            groupedRanges[matchingIndices[0]] = groupedRanges[matchingIndices[0]].expandTo(bookmarkRange);
        } else {
            let mergedRange = bookmarkRange;
            /**
             * EXAMPLE
             *   matchingIndices       = [1, 3]
             *   matchingIndices[0]    -> 1
             *   groupedBookmarks[1]   -> keep this bookmark name as the merged label
             */
            const mergedBookmark = groupedBookmarks[matchingIndices[0]];
            for (const idx of matchingIndices) {
                mergedRange = mergedRange.expandTo(groupedRanges[idx]);
            }

            /**
             * WHY
             *   Delete matched groups from highest index to lowest -> later splices do not
             *   shift earlier positions before they are removed.
             *
             * EXAMPLE
             *   matchingIndices   = [1, 3, 4]
             *   removal order     -> 4, then 3, then 1
             */
            for (let k = matchingIndices.length - 1; k >= 0; k--) {
                /**
                 * EXAMPLE
                 *   matchingIndices   = [1, 3, 4]
                 *   k                 = 1
                 *   matchingIndices[1]-> 3
                 *   splice(3, 1)      -> remove groupedRanges[3]
                 */
                groupedRanges.splice(matchingIndices[k], 1);
                /**
                 * EXAMPLE
                 *   matchingIndices      = [1, 3, 4]
                 *   k                    = 1
                 *   matchingIndices[1]   -> 3
                 *   splice(3, 1)         -> remove groupedBookmarks[3]
                 *
                 * WHY
                 *   Delete the bookmark label at the same index so both arrays stay aligned.
                 */
                groupedBookmarks.splice(matchingIndices[k], 1);
            }

            groupedRanges.push(mergedRange);
            groupedBookmarks.push(mergedBookmark);
        }
    }

    /**
     * WHEN at least one grouped range exists
     *   CONSEQUENCE
     *     Run one final sync so queued `expandTo` work is materialized before
     *     returning live range objects.
     */
    if (groupedRanges.length > 0) {
        await context.sync();
    }

    return { groupedRanges, groupedBookmarks };
}

const WORD_BOOKMARK_MAX_LENGTH = 40;
const WORD_BOOKMARK_NAME_PATTERN = new RegExp(
    `^[A-Za-z_][A-Za-z0-9_]{0,${WORD_BOOKMARK_MAX_LENGTH - 1}}$`
);

const INVALID_BOOKMARK_TAG_KEY = "error_type";
const INVALID_BOOKMARK_TAG_VALUE = "invalid_bookmark_name_from_agent";
const INVALID_BOOKMARK_CONTEXT_KEY = "invalid_bookmark_names";
const INVALID_BOOKMARK_MESSAGE = "Agent emitted bookmark names that violate Word naming rules";
const MISSING_BOOKMARKS_MESSAGE = "Could not find any of the bookmarks";
const UNWRITABLE_MERGE_ENTRY_MESSAGE = "Bulk apply prepared no writable change for the entry's bookmarks";
const UNWRITABLE_COMMENT_ENTRY_MESSAGE = "Bulk apply prepared no writable comment for the entry's bookmarks";
const COMMENTS_REFUSED_BY_WORD_TOAST = "Word refused to add comments to this document. The remaining comments were skipped.";
const CONSECUTIVE_COMMENT_REFUSALS_THAT_SKIP_THE_REST = 2;

function isValidWordBookmarkName(name: string): boolean {
    return WORD_BOOKMARK_NAME_PATTERN.test(name);
}

function reportInvalidBookmarkNames(received: string[], invalid: string[]): void {
    reportMessage(INVALID_BOOKMARK_MESSAGE, {
        level: "error",
        tags: { [INVALID_BOOKMARK_TAG_KEY]: INVALID_BOOKMARK_TAG_VALUE },
        contexts: { [INVALID_BOOKMARK_CONTEXT_KEY]: { invalid, received } },
    });
}

// Expanded range covering the bookmarks (may be disjoint/non-contiguous); null if none valid.
export const queueBookmarkRanges = (
    context: Word.RequestContext,
    bookmarkNames: string[]
): Word.Range[] => {
    const validBookmarkNames = bookmarkNames.filter(isValidWordBookmarkName);
    /**
     * WHEN at least one supplied bookmark name violates Word's naming rules
     *   CONSEQUENCE
     *     Report the invalid names, but still queue any names that are valid.
     */
    if (validBookmarkNames.length !== bookmarkNames.length) {
        const invalid = bookmarkNames.filter((name) => !isValidWordBookmarkName(name));
        reportInvalidBookmarkNames(bookmarkNames, invalid);
    }

    const allBookmarks = validBookmarkNames.map((name) => context.document.getBookmarkRangeOrNullObject(name));
    allBookmarks.forEach((bookmark) => bookmark.load("isNullObject"));
    return allBookmarks;
};

export const expandQueuedBookmarkRanges = (allBookmarks: Word.Range[]): Word.Range | null => {
    const nonNullBookmarks = allBookmarks.filter((bookmark) => !bookmark.isNullObject);
    const expandedRange = nonNullBookmarks.reduce<Word.Range | null>((acc, bookmark) => {
        const currentRange = bookmark.getRange();
        /**
         * WHEN the reducer already has a live range
         *   CONSEQUENCE
         *     Expand that running union to include the current bookmark range.
         */
        if (acc) {
            /**
             * WHEN this is the first non-null bookmark range in the reducer
             *   CONSEQUENCE
             *     Seed the union with its range.
             */
            return acc.expandTo(currentRange);
        } else {
            return currentRange;
        }
    }, null);

    /**
     * NULL
     *   No live bookmark range was found. Callers should treat this as
     *   "bookmarks resolved to nothing", not as a Word read failure.
     */
    return expandedRange ?? null;
};

export const getExpandedRangeForBookmarks = async (
    context: Word.RequestContext,
    bookmarkNames: string[]
): Promise<Word.Range | null> => {
    const bookmarkNamesAreMissingOrEmpty = (): boolean => {
        const bookmarkNamesAreMissing = !bookmarkNames;
        if (bookmarkNamesAreMissing) return true;
        return bookmarkNames.length === 0;
    };
    /**
     * WHEN no bookmark list was supplied
     *   CONSEQUENCE
     *     Return null before touching Word.
     */
    if (bookmarkNamesAreMissingOrEmpty()) return null;

    const allBookmarks = queueBookmarkRanges(context, bookmarkNames);
    /**
     * WHEN every supplied name was filtered out before lookup
     *   CONSEQUENCE
     *     Return null. No Word bookmark lookup can succeed.
     */
    if (allBookmarks.length === 0) return null;
    await context.sync();

    return expandQueuedBookmarkRanges(allBookmarks);
};

export const getBookmarksBySelectedText = (): Promise<string[]> => {

    return withWordContextRead(async (context) => {
        const selection = context.document.getSelection();
        const bookmarkNames = selection.getBookmarks(true, false);
        selection.load("text");

        await context.sync().catch((error) => {
            console.error("Error getting bookmarks from selected text:", error);
        });

        const bookmarkRanges = bookmarkNames.value.map(bookmarkName => {
            const bookmarkRange = context.document.getBookmarkRangeOrNullObject(bookmarkName);
            bookmarkRange.load("text");
            return bookmarkRange;
        })

        await context.sync();

        const matchingBookmarks: string[] = []
        for (let i = 0; i < bookmarkRanges.length; i++) {
            const bookmarkRange = bookmarkRanges[i];
            /**
             * WHEN the bookmark's full text equals the current selection text
             *   CONSEQUENCE
             *     Keep this bookmark.
             *
             * WHY
             *   Exclude enclosing bookmarks that only contain the selection.
             */
            if (
                bookmarkRange.text === selection.text
            ) {
                matchingBookmarks.push(bookmarkNames.value[i]);
            }
        }

        return matchingBookmarks;
    })
}

export const getDocumentFileToBase64 = (readProgress: DocumentReadObserver = UNOBSERVED_DOCUMENT_READ): Promise<string> => {
    pendingForegroundWork += 1;
    readProgress.enterStep("read_lock_wait");
    return readDocumentFileToBase64(readProgress).finally(() => {
        pendingForegroundWork -= 1;
    });
};

const readDocumentFileToBase64 = (readProgress: DocumentReadObserver) => withOfficeContextRead((context) => {
    readProgress.enterStep("get_file");
    return new Promise<string>((resolve, reject) => {

        context.document.getFileAsync(Office.FileType.Compressed, async (result) => {
            /**
             * WHEN Office returned a compressed document file handle
             *   CONSEQUENCE
             *     Continue with slice-by-slice reading.
             */
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                const file = result.value;
                try {
                    const sliceCount = file.sliceCount;
                    readProgress.fileOpened({ documentBytes: file.size, sliceCount });
                    readProgress.enterStep("read_slices");
                    const docdataSlices: Uint8Array[] = [];

                    for (let i = 0; i < sliceCount; i++) {
                        const sliceData = await new Promise<Uint8Array>((resolveSlice, rejectSlice) => {
                            try {
                                file.getSliceAsync(i, (sliceResult) => {
                                    /**
                                     * WHEN this slice fetch succeeded
                                     *   CONSEQUENCE
                                     *     Accept its bytes at this slice index.
                                     */
                                    if (sliceResult.status === Office.AsyncResultStatus.Succeeded) {
                                        /**
                                         * WHEN this slice fetch failed
                                         *   CONSEQUENCE
                                         *     Reject the outer read so the export aborts instead of returning a
                                         *     truncated document.
                                         */
                                        resolveSlice(sliceResult.value.data);
                                    } else {
                                        rejectSlice(new Error(`getSliceAsync failed for slice ${i}: ${sliceResult.error.message}`));
                                    }
                                });
                            } catch (e) {
                                rejectSlice(e);
                            }
                        });

                        docdataSlices[i] = sliceData;
                        readProgress.sliceRead();
                    }

                    const totalLength = docdataSlices.reduce((sum, slice) => sum + slice.length, 0);
                    const docdata = new Uint8Array(totalLength);
                    let offset = 0;
                    for (const slice of docdataSlices) {
                        docdata.set(slice, offset);
                        offset += slice.length;
                    }

                    readProgress.enterStep("close_file");
                    file.closeAsync((closeResult) => {
                        /**
                         * WHEN closing the temporary file handle failed after assembly
                         *   CONSEQUENCE
                         *     Warn only. The payload is already complete.
                         */
                        if (closeResult.status !== Office.AsyncResultStatus.Succeeded) {
                            console.warn("Failed to close file", closeResult.error);
                        }
                        readProgress.enterStep("encode_base64");
                        new Promise<string>((resolveBase64, rejectBase64) => {
                            const reader = new FileReader();
                            /**
                             * EXAMPLE
                             *   reader.result      = "data:application/octet-stream;base64,QUJD"
                             *   split(",")         -> ["data:application/octet-stream;base64", "QUJD"]
                             *   [1]                -> "QUJD"
                             */
                            reader.onload = () => resolveBase64((reader.result as string).split(",")[1]);
                            reader.onerror = () => rejectBase64(new Error('Failed to read document as base64'));
                            reader.readAsDataURL(new Blob([docdata]));
                        })
                            .then(resolve)
                            .catch(reject);
                    });
                } catch (e) {
                    file.closeAsync((closeResult) => {
                        /**
                         * WHEN close fails on the error path
                         *   CONSEQUENCE
                         *     Warn, then reject with the original read error.
                         */
                        if (closeResult.status !== Office.AsyncResultStatus.Succeeded) {
                            console.warn("Failed to close file", closeResult.error);
                        }
                        /**
                         * WHEN Office could not open the compressed document file
                         *   CONSEQUENCE
                         *     Reject immediately. There are no slices to read.
                         */
                        reject(e);
                    });
                }
            } else {
                console.warn("Failed to read Word document ", result.error);
                reject(new Error("Failed to read Word document: " + result.error?.message));
            }
        });
    });
});

export const replaceDocumentFromBase64 = (docBase64: string) => withWordContextWrite(async (context) => {
    BodyReplacement.announce("begin");
    try {
        await withTemporaryTrackingMode(context, Word.ChangeTrackingMode.off, async () => {
            context.document.insertFileFromBase64(docBase64, Word.InsertLocation.replace, {
                importChangeTrackingMode: true,
                importCustomProperties: true,
                importCustomXmlParts: true,
                importDifferentOddEvenPages: true,
                importPageColor: true,
                importParagraphSpacing: true,
                importStyles: true,
                importTheme: true,
            });
            await context.sync();
        });
        await context.sync();
    } finally {
        BodyReplacement.announce("end");
    }
});

export const getDocumentSectionOoxml = () => {

    return withWordContextRead(async (context) => {
        const selection = context.document.getSelection();
        const ooxml = selection.getOoxml();
        await context.sync();
        // eslint-disable-next-line office-addins/load-object-before-read
        return (ooxml as any).value;
    });
};

// Plain text of the current selection with tracked changes applied (as if all
// revisions were accepted) — used to save clauses without revision markup.
export const getSelectedReviewedText = (): Promise<string> => {
    return withWordContextRead(async (context) => {
        const selection = context.document.getSelection();
        const reviewed = selection.getReviewedText(Word.ChangeTrackingVersion.current);
        await context.sync();
        return reviewed.value;
    });
};

// Inserts plain text at the cursor (replacing any selection) with insertText,
// so it inherits the formatting of the destination range. Resolves to true on
// success, false if Word rejected the insert — callers use this to surface a
// success/error toast.
export const insertTextAtCursor = (content: string): Promise<boolean> => {
    return withWordContextWrite(async (context) => {
        const selection = context.document.getSelection();
        selection.insertText(content, Word.InsertLocation.replace);
        await context.sync();
        return true;
    }).catch((error) => {
        console.error("Error: " + error);
        /**
         * WHEN the insert failure came from Office JS
         *   CONSEQUENCE
         *     Log `debugInfo`, which exists only on that error type.
         */
        if (error instanceof OfficeExtension.Error) {
            console.error("Debug info: " + JSON.stringify(error.debugInfo));
        }
        return false;
    });
};

// Inserts OOXML at the cursor (replacing any selection). Resolves to true on
// success, false if Word rejected the insert.
export const insertOoxmlAtCursor = (ooxmlString: string): Promise<boolean> => {

    return withWordContextWrite(async (context) => {
        // Plain insert at the cursor/selection — respects the document's current
        // tracking mode rather than forcing a tracked change.
        const selection = context.document.getSelection();
        selection.insertOoxml(ooxmlString, Word.InsertLocation.replace);

        await context.sync();
        return true;
    }).catch((error) => {
        console.error("Error: " + error);
        /**
         * WHEN the OOXML insert failure came from Office JS
         *   CONSEQUENCE
         *     Log `debugInfo`, which generic errors do not carry.
         */
        if (error instanceof OfficeExtension.Error) {
            console.error("Debug info: " + JSON.stringify(error.debugInfo));
        }
        return false;
    });
};

const cleanOoxml = (ooxml: string): string => {
    const cleaned = ooxml.trim()
        .replace(/<\?xml[^>]*\?>/g, '')
        .replace(/<!DOCTYPE[^>]*>/g, '');

    /**
     * CASES
     *   - already has `<w:document`  -> return unchanged
     *   - fragment only              -> wrap in `<w:document><w:body>...`
     */
    return cleaned.includes('<w:document') ? cleaned :
        `<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
      <w:body>${cleaned}</w:body>
    </w:document>`;
};

const validateOoxml = (ooxml: string): void => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(ooxml, "text/xml");

    /**
     * WHEN XML parsing produced a `parsererror` node
     *   CONSEQUENCE
     *     Reject this OOXML as structurally invalid before merge logic mutates it.
     */
    if (doc.querySelector('parsererror')) {
        console.error('OOXML parsing error:', doc.querySelector('parsererror')?.textContent);
        throw new Error('Invalid OOXML structure');
    }
};

const createPackageOoxml = (content: string): string => `
  <pkg:package xmlns:pkg='http://schemas.microsoft.com/office/2006/xmlPackage'>
    <pkg:part pkg:name='/_rels/.rels' pkg:contentType='application/vnd.openxmlformats-package.relationships+xml' pkg:padding='512'>
      <pkg:xmlData>
        <Relationships xmlns='http://schemas.openxmlformats.org/package/2006/relationships'>
          <Relationship Id='rId1' Type='http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument' Target='word/document.xml'/>
        </Relationships>
      </pkg:xmlData>
    </pkg:part>
    <pkg:part pkg:name='/word/document.xml' pkg:contentType='application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml'>
      <pkg:xmlData>${content}</pkg:xmlData>
    </pkg:part>
  </pkg:package>`;

const expandToEnclosingParagraphs = async (range: Word.Range): Promise<Word.Range> => {
    const context = range.context;
    const paragraphs = range.paragraphs;
    paragraphs.load("items");
    await context.sync();
    /**
     * WHEN Word reports no enclosing paragraphs for the range
     *   CONSEQUENCE
     *     Return the original range unchanged.
     */
    if (paragraphs.items.length === 0) return range;

    /**
     * EXAMPLE
     *   paragraphs.items.length = 3
     *   items[0]                -> first paragraph range
     *   later iterations        -> expand to include items[1] and items[2]
     */
    let expanded = paragraphs.items[0].getRange();
    for (let i = 1; i < paragraphs.items.length; i++) {
        expanded = expanded.expandTo(paragraphs.items[i].getRange());
    }
    return expanded;
};

const mergeParagraphs = async (range: Word.Range, newOoxml: string): Promise<void> => {
    const context = range.context;

    range.load("text");
    await context.sync();

    // Word cannot merge OOXML into an empty range — Replace becomes
    // insert-at-point, surrounding paragraphs survive, and re-applying the
    // same redline duplicates the paragraph.
    if ((range.text?.length || 0) === 0) {
        range.insertText(" ", Word.InsertLocation.start);
        await context.sync();
    }

    const currentOoxml = range.getOoxml();
    await context.sync();

    // eslint-disable-next-line office-addins/load-object-before-read
    const cleanedCurrentOoxml = cleanOoxml(currentOoxml.value);
    const cleanedNewOoxml = cleanOoxml(newOoxml);

    validateOoxml(cleanedCurrentOoxml);
    validateOoxml(cleanedNewOoxml);

    const parser = new DOMParser();
    const currentDoc = parser.parseFromString(cleanedCurrentOoxml, "text/xml");
    const newDoc = parser.parseFromString(cleanedNewOoxml, "text/xml");

    const currentParagraphs = Array.from(currentDoc.getElementsByTagName("w:p"));
    const newParagraphs = Array.from(newDoc.getElementsByTagName("w:p"));

    currentParagraphs.forEach(p => p.parentNode?.removeChild(p));

    /**
     * EXAMPLE
     *   getElementsByTagName("w:body") -> NodeList with the document body
     *   [0]                            -> that first body element
     */
    const body = currentDoc.getElementsByTagName("w:body")[0];
    /**
     * WHEN the parsed OOXML has no `<w:body>` element
     *   CONSEQUENCE
     *     Throw immediately. Paragraph replacement cannot proceed without the
     *     document body container.
     */
    if (!body) throw new Error("No body element found in current document");

    const importedParagraphs = newParagraphs.map(p => {
        const importedNode = currentDoc.importNode(p, true) as Element;
        body.appendChild(importedNode);
        return importedNode;
    });

    preserveParagraphBookmarks(currentParagraphs, importedParagraphs, currentDoc);

    const modifiedOoxml = new XMLSerializer().serializeToString(currentDoc);
    range.insertOoxml(modifiedOoxml, Word.InsertLocation.replace);
    await context.sync();
};

/**
 * Strips all SDT lock elements from OOXML to allow programmatic editing.
 *
 * Word SDTs support four lock modes via `<w:lock w:val="..."/>`:
 *   - sdtContentLocked: content cannot be edited, SDT cannot be deleted
 *   - contentLocked: content cannot be edited, SDT can be deleted
 *   - sdtLocked: content can be edited, SDT cannot be deleted
 *   - unlocked: no restrictions (default when element is omitted)
 *
 * Any of the non-"unlocked" modes will cause `Range.insertOoxml` and
 * `Range.delete` to throw a GeneralException when the range overlaps
 * the locked SDT. Stripping the lock elements makes them all behave
 * as "unlocked".
 */
const stripSdtLocks = (ooxml: string): string => {
    return ooxml.replace(/<w:lock\s+w:val="[^"]*"\s*\/>/g, '');
};

interface WordMergeErrorInfo {
    code?: string;
    message?: string;
    location?: string;
}

const extractWordError = (error: unknown): WordMergeErrorInfo | undefined => {
    /**
     * WHEN the failure is an Office JS exception
     *   CONSEQUENCE
     *     Extract Word-specific code and error location from its diagnostic
     *     payload.
     */
    if (error instanceof OfficeExtension.Error) {
        return {
            code: error.code,
            message: error.message,
            location: error.debugInfo?.errorLocation,
        };
    }
    /**
     * WHEN the failure is a plain JavaScript Error
     *   CONSEQUENCE
     *     Return only its message. No Office diagnostics exist.
     */
    if (error instanceof Error) {
        return { message: error.message };
    }
    return undefined;
};

const verifyAppliedPackage = async (
    context: Word.RequestContext,
    range: Word.Range,
    targetPackageOoxml: string,
    bookmarks: string[] | undefined,
    insertAfterRange: boolean,
): Promise<void> => {
    if (targetPackageOoxml.includes("<w:tbl")) return;
    try {
        const appliedOoxml = range.getOoxml();
        await context.sync();

        // eslint-disable-next-line office-addins/load-object-before-read
        const actualBody = extractBodyContent(appliedOoxml.value);
        const targetBody = extractBodyContent(targetPackageOoxml);
        const outcome = verifyRecovery({ targetBody, actualBody });
        const expectedRedlines = redlineSignature(targetBody);
        const actualRedlines = redlineSignature(actualBody);
        const droppedRedlines = redlinesDropped(expectedRedlines, actualRedlines);

        if (outcome.status === "clean" && !droppedRedlines) {
            console.info(
                `[OOXML_APPLY_VERIFY] clean (distance=${outcome.distance}, ` +
                `ins=${actualRedlines.ins}, del=${actualRedlines.del}, ` +
                `bookmarks=${bookmarks?.join(",") || "(selection)"})`,
            );
            return;
        }

        const divergence = outcome.status === "drift"
            ? describeFirstDivergence(outcome.expectedText, outcome.actualText)
            : null;
        const reason = outcome.status === "drift"
            ? outcome.reason
            : `applied range holds ins:${actualRedlines.ins}/del:${actualRedlines.del} tracked changes, payload holds ins:${expectedRedlines.ins}/del:${expectedRedlines.del}`;

        console.warn(`[OOXML_APPLY_DRIFT] insertOoxml reported success but the applied range diverges: ${reason}`, {
            expectedRedlines, actualRedlines, divergence, bookmarks, insertAfterRange,
        });

        reportError(new Error(`OOXML apply drift: ${reason}`), {
            level: "warning",
            tags: { error_type: "ooxml_apply_drift" },
            contexts: {
                ooxml_apply_drift: {
                    reason,
                    distance: outcome.distance,
                    expectedRedlines: `ins:${expectedRedlines.ins}/del:${expectedRedlines.del}`,
                    actualRedlines: `ins:${actualRedlines.ins}/del:${actualRedlines.del}`,
                    expectedTextLength: outcome.status === "drift" ? outcome.expectedText.length : null,
                    actualTextLength: outcome.status === "drift" ? outcome.actualText.length : null,
                    bookmarks,
                    insertAfterRange,
                    targetReferencesHeaders: targetPackageOoxml.includes("w:headerReference"),
                },
            },
        });
    } catch (verifyError) {
        console.warn("[OOXML_APPLY_VERIFY] verification skipped", verifyError);
    }
};

const mergePackages = async (
    range: Word.Range,
    newOoxml: string,
    insertAfterRange: boolean,
    applyContext: RedlineApplyContext,
    recorder: RedlineAttemptRecorder,
    bookmarks: string[],
): Promise<Word.Range> => {
    const context = range.context;

    range.load("isNullObject,text");
    await context.sync();

    /**
     * WHEN the bookmark range has become a null object
     *   CONSEQUENCE
     *     Record a bookmark-range failure and stop before any OOXML write.
     */
    if (range.isNullObject) {
        const nullRangeError = new UserFacingError("The bookmark location no longer exists in the document. This change cannot be applied.",
            new Error("Range is null - bookmark may have been deleted"));
        recorder.recordFailureAndKeepItsSnapshotId({
            applyContext,
            failure: { stage: "bookmark_range_null", bookmarks, attemptedOoxml: newOoxml },
            error: nullRangeError,
        });
        throw nullRangeError;
    }

    let liveDocumentOoxml: string | undefined;
    /**
     * WHEN the incoming redline touches comment-thread state
     *   CONSEQUENCE
     *     Capture live document OOXML so resolve/open state can be restored after
     *     merge.
     */
    if (redlineCarriesCommentThreads(newOoxml)) {
        try {
            const liveResult = context.document.body.getOoxml();
            await context.sync();
            liveDocumentOoxml = liveResult.value;
        } catch (liveReadError) {
            throw new UserFacingError(
                "We could not confirm the document's current comment state, so this change was not applied. Please try again.",
                toError(liveReadError),
            );
        }
    }

    /**
     * WHEN this apply uses paragraph-add semantics
     *   CONSEQUENCE
     *     Insert an empty paragraph after the anchor and switch the write target to
     *     that new paragraph range.
     */
    let insertedParagraph: Word.Paragraph | null = null;
    if (insertAfterRange) {
        insertedParagraph = range.insertParagraph("", Word.InsertLocation.after);
        range = insertedParagraph.getRange();
    }

    range.load("text");
    await context.sync();

    const rangeTextLength = range.text?.length || 0; // eslint-disable-line office-addins/load-object-before-read

    // Word cannot merge OOXML into an empty range
    let insertedPlaceholder: Word.Range | null = null;
    if (rangeTextLength === 0) {
        insertedPlaceholder = range.insertText(" ", Word.InsertLocation.start);
        await context.sync();
    }

    const currentOoxml = range.getOoxml();
    await context.sync();

    // eslint-disable-next-line office-addins/load-object-before-read
    const currentOoxmlValue = PackageBinaryParts.withoutEmbeddedFonts(currentOoxml.value);
    let mergedOoxml = mergeOoxmlPackages(currentOoxmlValue, newOoxml);
    /**
     * WHEN live document OOXML was captured earlier
     *   CONSEQUENCE
     *     Restore live comment resolve state onto the merged package before insert.
     */
    if (liveDocumentOoxml !== undefined) {
        mergedOoxml = restoreLiveResolveState(mergedOoxml, liveDocumentOoxml);
    }

    const unresolvedReferences = findUnresolvedPackageReferences(mergedOoxml);
    if (unresolvedReferences.length > 0) {
        if (insertedParagraph !== null) {
            insertedParagraph.delete();
            await context.sync();
        } else if (insertedPlaceholder !== null) {
            insertedPlaceholder.delete();
            await context.sync();
        }
        const detail = formatUnresolvedPackageReferences(unresolvedReferences);
        reportMessage("insertOoxml preflight rejected the merged package", { level: "error", extra: { mergeStep: "preflight_merged_package", detail } });
        throw new OoxmlMergeError(`merged package references parts it does not contain: ${detail}`, {
            mergeStep: "preflight_merged_package",
            failedOoxml: newOoxml,
            currentRangeOoxml: currentOoxmlValue,
            wordError: { message: detail },
        });
    }

    // Delete tables before re-inserting because range.insertOoxml won't replace them
    const tables = range.tables.load("items");
    await context.sync();
    /**
     * WHEN the target range contains tables
     *   CONSEQUENCE
     *     Delete them before `insertOoxml(..., replace)`.
     *
     * WHY
     *   Word does not reliably replace existing tables in place.
     */
    if (tables.items.length > 0) {
        for (const table of tables.items) {
            table.delete();
        }
        await context.sync();
    }

    try {
        range.insertOoxml(mergedOoxml, Word.InsertLocation.replace);
        await context.sync();
    } catch (error) {
        const wordError = extractWordError(error);
        try {
            range.insertOoxml(currentOoxmlValue, Word.InsertLocation.replace);
            await context.sync();
        } catch (revertError) {
            throw new OoxmlMergeError(
                `Failed to insert merged OOXML and revert failed. Original error: ${error instanceof Error ? error.message : String(error)}. Revert error: ${revertError instanceof Error ? revertError.message : String(revertError)}`,
                {
                    mergeStep: 'insert_merged_and_revert',
                    /**
                     * CASES
                     *   - `error` is an Error      -> keep it as the cause
                     *   - non-Error throw          -> wrap with `new Error(String(error))`
                     */
                    cause: error instanceof Error ? error : new Error(String(error)),
                    failedOoxml: mergedOoxml,
                    currentRangeOoxml: currentOoxmlValue,
                    wordError,
                }
            );
        }
        throw new OoxmlMergeError(
            error instanceof Error ? error.message : String(error),
            {
                mergeStep: 'insert_merged',
                /**
                 * CASES
                 *   - `error` is an Error      -> keep it as the cause
                 *   - non-Error throw          -> wrap with `new Error(String(error))`
                 */
                cause: error instanceof Error ? error : new Error(String(error)),
                failedOoxml: mergedOoxml,
                currentRangeOoxml: currentOoxmlValue,
                wordError,
            }
        );
    }

    return range;
};

/**
 * Unlocks all content controls within the range and any ancestor
 * content controls that wrap it, allowing programmatic edits.
 */
const unlockRange = async (range: Word.Range): Promise<void> => {
    const context = range.context;

    const childControls = range.contentControls;
    childControls.load("cannotEdit, cannotDelete");
    await context.sync();

    childControls.items.forEach((control) => {
        control.cannotEdit = false;
        control.cannotDelete = false;
    });

    let parent = range.parentContentControlOrNullObject;
    parent.load("isNullObject, cannotEdit, cannotDelete");
    await context.sync();

    while (!parent.isNullObject) {
        parent.cannotEdit = false;
        parent.cannotDelete = false;
        parent = parent.parentContentControlOrNullObject;
        parent.load("isNullObject, cannotEdit, cannotDelete");
        await context.sync();
    }
};

// One backend attempt per failure. The previous value (2) added latency
// without measurable benefit — the second attempt almost never succeeded
// when the first didn't.
const MAX_OOXML_REPAIR_ATTEMPTS = 1;

export interface ApplyOoxmlChangesOptions {
    /** 1-indexed during an active repair, `null` once recovery is done. */
    onRepairStatus?: (attempt: number | null) => void;
}

interface ApplyOoxmlChangesInternalOptions extends ApplyOoxmlChangesOptions {
    origin: PackageOrigin;
    repairAttempt?: number;
    priorAttemptSummary?: string;
}

const repairOoxmlViaBackend = async (
    authHeader: string,
    error: OoxmlMergeError,
    attemptNumber: number,
    priorAttemptSummary: string | undefined,
): Promise<{ repairedOoxml: string; repairSummary: string }> => {
    const result = await repairMergeError(authHeader, {
        failedOoxml: error.failedOoxml,
        currentRangeOoxml: error.currentRangeOoxml,
        mergeStep: error.mergeStep,
        wordError: error.wordError ?? {},
        attemptNumber,
        priorAttemptSummary,
    });
    return { repairedOoxml: result.repaired_ooxml, repairSummary: result.repair_summary };
};

/**
 * Apply an OOXML payload to a Word range with backend OOXML repair as
 * the first line of defense on failure. Throws `OoxmlMergeError` if both
 * the initial merge AND backend repair fail — the caller can then run an
 * agent-driven fallback *outside* this function's write lock. Most call
 * sites should use `applyRedlineOoxml` below, which owns the lock + range
 * resolution + agent fallback in one call.
 */
export const applyOoxmlChanges = async (
    context: Word.RequestContext,
    range: Word.Range,
    ooxml: string,
    insertAfterRange: boolean,
    applyContext: RedlineApplyContext,
    recorder: RedlineAttemptRecorder,
    bookmarks: string[] | undefined,
    options: ApplyOoxmlChangesInternalOptions,
): Promise<void> => {
    /**
     * WHEN the OOXML payload is blank or whitespace only
     *   CONSEQUENCE
     *     Throw immediately with a specific validation error.
     */
    if (!ooxml?.trim()) throw new Error('Invalid OOXML: must be a non-empty string');

    const { onRepairStatus, repairAttempt = 0, priorAttemptSummary, origin } = options;
    const apiKey = getApiKey();
    /** The legacy fixer needs the API key and runs for pipeline markup only; an engine package never goes to it. */
    const repairEnabled = origin === "pipeline" && Boolean(apiKey);

    try {
        await unlockRange(range);
    } catch {
        // Non-fatal: proceed even if unlocking fails
    }

    ooxml = stripSdtLocks(ooxml);

    // For "at" applies, expand to enclosing paragraphs so a sub-paragraph
    // bookmark range gets fully replaced (otherwise outside-range content
    // remains in place and produces duplication on re-apply).
    if (!insertAfterRange) {
        range = await expandToEnclosingParagraphs(range);

        /**
         * WHEN the package is the server pipeline's markup
         *   CONSEQUENCE
         *     the prod untracked-write guard compares it with the range first, as it
         *     always has.
         * WHEN the package is the add-in engine's own output
         *   CONSEQUENCE
         *     no guard: the engine writes tracked changes by construction, and the
         *     guard's original-view comparison does not model a composed table
         *     package (2026-09-22: it snapshotted two of them for nothing).
         */
        if (origin === "pipeline") {
            const rangeOoxml = range.getOoxml();
            await context.sync();
            const { value: rangeOoxmlValue } = rangeOoxml;
            guardUntrackedWrite({
                currentOoxml: rangeOoxmlValue,
                incomingOoxml: ooxml,
                applyContext,
                bookmarks: bookmarks ?? [],
                recorder,
            });
        }
    }

    try {
        await withTemporaryTrackingMode(context, Word.ChangeTrackingMode.off, async () => {
            // No raw-insertion fallback for the pkg:package branch: that
            // would bypass auxiliary-parts substitution and could destroy
            // footnotes/endnotes/comments from other paragraphs.
            if (ooxml.includes("pkg:package")) {
                /**
                 * FALLBACK
                 *   First try paragraph-level merge for non-package OOXML.
                 *   If that fails, wrap the fragment as a temporary package and insert that
                 *   instead.
                 */
                range = await mergePackages(range, ooxml, insertAfterRange, applyContext, recorder, bookmarks ?? []);
            } else {
                try {
                    await mergeParagraphs(range, ooxml);
                } catch {
                    const cleanedOoxml = cleanOoxml(ooxml);
                    range.insertOoxml(createPackageOoxml(cleanedOoxml), Word.InsertLocation.replace);
                }
            }
            range.select();
            await context.sync();
        });
        if (ooxml.includes("pkg:package")) {
            await verifyAppliedPackage(context, range, ooxml, bookmarks, insertAfterRange);
        }
        return;
    } catch (mergeError) {
        /**
         * WHEN the untracked-write guard refused this apply
         *   CONSEQUENCE
         *     Re-throw unchanged.
         *
         * WHY
         *   This is a policy stop, not an OOXML merge failure to repair.
         */
        if (mergeError instanceof UntrackedWriteRefused) throw mergeError;

        /**
         * WHEN the failure is an `OoxmlMergeError`
         *   CONSEQUENCE
         *     Run OOXML-specific recording and optional repair flow.
         *     Other error types skip that path.
         */
        if (mergeError instanceof OoxmlMergeError) {
            // Stash bookmarks on the error so the agent fallback (run by
            // the caller, outside our write lock) can address the
            // paragraph by stable Dioptra ids.
            mergeError.bookmarks = bookmarks;
            const shouldTryBackendRepair = (): boolean => {
                if (!repairEnabled) return false;
                return repairAttempt < MAX_OOXML_REPAIR_ATTEMPTS;
            };

            /**
             * WHEN repair is enabled and attempts remain
             *   CONSEQUENCE
             *     Call backend repair with the next attempt number.
             */
            if (shouldTryBackendRepair()) {
                const nextAttempt = repairAttempt + 1;
                onRepairStatus?.(nextAttempt);
                try {
                    const { repairedOoxml, repairSummary } = await repairOoxmlViaBackend(
                        `Bearer ${apiKey}`,
                        mergeError,
                        nextAttempt,
                        priorAttemptSummary,
                    );
                    await applyOoxmlChanges(context, range, repairedOoxml, insertAfterRange, applyContext, recorder, bookmarks, {
                        ...options,
                        repairAttempt: nextAttempt,
                        priorAttemptSummary: repairSummary,
                    });
                    onRepairStatus?.(null);
                    return;
                } catch (repairError) {
                    onRepairStatus?.(null);
                const repairErrorMessage = repairError instanceof Error ? repairError.message : String(repairError);
                const repairFailureLogContext = {
                    repairAttempt: nextAttempt,
                    repairError: repairErrorMessage,
                };
                    console.error('[OOXML_MERGE_REPAIR_FAILURE] Backend repair attempt failed.', repairFailureLogContext);
                    recorder.recordFailureAndKeepItsSnapshotId({
                        applyContext,
                        failure: {
                            stage: "backend_repair_exhausted",
                            bookmarks: bookmarks ?? [],
                            ooxml: {
                                attemptedOoxml: mergeError.failedOoxml,
                                currentRangeOoxml: mergeError.currentRangeOoxml,
                            },
                            wordError: normalizeWordErrorFields(mergeError.wordError),
                            repairAttempts: nextAttempt,
                            repairFailureMessage: repairError instanceof Error ? repairError.message : String(repairError),
                        },
                        error: mergeError,
                    });
                    // Fall through: re-throw the original OoxmlMergeError
                    // so the caller can run agent recovery outside the
                    // surrounding write lock.
                }
            }

            console.error("[OOXML_MERGE_FAILURE] Failed to apply OOXML changes.", {
                mergeStep: mergeError.mergeStep, mergeError: mergeError.message,
            });
            recorder.recordFailureAndKeepItsSnapshotId({
                applyContext,
                failure: {
                    stage: "merge_failed",
                    mergeStep: mergeError.mergeStep,
                    bookmarks: bookmarks ?? [],
                    ooxml: {
                        attemptedOoxml: mergeError.failedOoxml,
                        currentRangeOoxml: mergeError.currentRangeOoxml,
                    },
                    wordError: normalizeWordErrorFields(mergeError.wordError),
                },
                error: mergeError,
            });
            throw mergeError;
        }

        /**
         * WHEN a non-OOXML failure came directly from Office JS
         *   CONSEQUENCE
         *     Log its `debugInfo` for host-level diagnosis.
         */
        if (mergeError instanceof OfficeExtension.Error) {
            console.error("Error applying changes:", JSON.stringify(mergeError.debugInfo));
        }
        throw mergeError;
    }
};

/**
 * Outcome reported via `onStatus`:
 *   - `in_progress`: a repair (backend or agent) has started.
 *   - `success`:     the repair finished cleanly (no warning needed).
 *   - `warning`:     the agent fallback ran and reported drift — caller
 *                    should mark the redline as needing a double-check.
 *   - `failed`:      both lines of defense failed; the function ALSO
 *                    throws a `UserFacingError`. The status fires first
 *                    so callers can release UI state in one place
 *                    without an explicit try/finally.
 *
 * Not signalled when the initial merge succeeds — no repair happened.
 */
export type RepairStatus = 'in_progress' | 'success' | 'warning' | 'failed';

export interface RunAgentRecoveryArgs {
    error: OoxmlMergeError;
    applyContext: RedlineApplyContext;
    recorder: RedlineAttemptRecorder;
}

export interface ApplyRedlineOoxmlArgs {
    ooxml: string;
    /** Which gesture started this apply, and the review it belongs to —
     *  carried onto every failure record and the apply funnel. */
    applySource: RedlineApplySource;
    /** Dioptra paragraph bookmarks (`_dioptra_p_*`) that locate the target
     *  paragraph. If omitted, the document's current selection is used. */
    bookmarks?: string[];
    /** When `true` with `bookmarks`, insert *after* the resolved range
     *  (paragraph-add semantics). Ignored when `bookmarks` is omitted. */
    insertAfter?: boolean;
    /** Single channel for repair lifecycle (start + terminal outcome). */
    onStatus?: (status: RepairStatus) => void;
    /** Agent fallback, threaded in by `useApplyRedlineOoxml` —
     *  intentionally not part of any caller-facing surface. */
    runAgentRecovery?: (args: RunAgentRecoveryArgs) => Promise<RecoveryOutcome>;
    /** Pipeline markup gets the prod guard and repairs; an engine package gets neither and fails at once on refusal. */
    origin: PackageOrigin;
}

/**
 * High-level OOXML apply: opens a write lock, resolves the target range
 * from bookmarks (or falls back to the current selection), runs
 * `applyOoxmlChanges` (which itself does backend repair on failure), and
 * — outside the write lock — dispatches the optional agent fallback when
 * `OoxmlMergeError` survives backend repair.
 *
 * Returns `true` when the merge can be considered applied (clean merge
 * or recovery completed without throwing); `false` only when the range
 * could not be resolved (e.g. bookmarks gone). Throws `UserFacingError`
 * on hard failure of both lines of defense.
 */
export const applyRedlineOoxml = async (args: ApplyRedlineOoxmlArgs): Promise<boolean> => {
    const { ooxml, applySource, bookmarks, insertAfter = false, onStatus, runAgentRecovery, origin } = args;

    const recorder = createRedlineAttemptRecorder(applySource);
    // Captured under the read lock, before the exclusive lock below, so the
    // bytes are provably the document as it was before we touched it.
    const applyCapture = await timedBulkApplySpan("single_entry_snapshot_captured", () => captureRedlineSnapshot());
    const applyAttemptStart = {
        capture: applyCapture,
        reviewId: readReviewIdOfApplySource(applySource),
    };
    const applyContext = startApplyAttempt(applyAttemptStart);

    let mergeFailure: OoxmlMergeError | null = null;
    let rangeResolved = true;
    let inProgress = false;
    let terminalReported = false;
    let outcomeRecorded = false;

    const recordOutcomeOnce = (outcome: RedlineAttemptOutcome) => {
        /**
         * WHEN an attempt outcome was already recorded
         *   CONSEQUENCE
         *     Do nothing.
         *
         * WHY
         *   Success, warning, failure, and finally paths all converge here. Record only
         *   once.
         */
        if (outcomeRecorded) return;
        outcomeRecorded = true;
        recorder.recordAttemptEndedAs(outcome);
    };

    const setInProgress = () => {
        /**
         * WHEN no repair path has reported progress yet
         *   CONSEQUENCE
         *     Mark repair as in progress and emit `in_progress` once.
         */
        if (!inProgress) { inProgress = true; onStatus?.('in_progress'); }
    };

    const describeWhyTheRangeIsUnresolved = (names: string[]): { failure: RedlineFailure; error: Error } => {
        const invalidBookmarks = names.filter((name) => !isValidWordBookmarkName(name));
        /**
         * WHEN at least one bookmark name is syntactically invalid
         *   CONSEQUENCE
         *     Report `invalid_bookmark_name` instead of ordinary drift.
         */
        if (invalidBookmarks.length > 0) {
            return {
                failure: { stage: 'invalid_bookmark_name', bookmarks: names, invalidBookmarks, attemptedOoxml: ooxml },
                error: new Error(`Bookmark names violate Word naming rules: ${invalidBookmarks.join(', ')}`),
            };
        }
        return {
            failure: { stage: 'range_unresolved', bookmarks: names, attemptedOoxml: ooxml },
            error: new Error(MISSING_BOOKMARKS_MESSAGE),
        };
    };

    const reportTerminal = (status: RepairStatus) => {
        /**
         * WHEN a terminal UI status was already sent
         *   CONSEQUENCE
         *     Do nothing. Success, warning, and failure are terminal once.
         */
        if (terminalReported) return;
        terminalReported = true;
        onStatus?.(status);
    };

    try {
        await withWordContextWrite(async (context) => {
            let range: Word.Range | null;
            const bookmarksWereSupplied = Boolean(bookmarks);
            const bookmarkNamesWereSupplied = bookmarksWereSupplied && bookmarks.length > 0;
            /**
             * CASES
             *   - bookmarks supplied     -> resolve the target from those bookmarks
             *   - no bookmarks supplied  -> use the current selection
             */
            if (bookmarkNamesWereSupplied) {
                const base = await getExpandedRangeForBookmarks(context, bookmarks);
                /**
                 * WHEN no live range could be resolved from the target bookmarks
                 *   CONSEQUENCE
                 *     Mark the apply as unresolved and return without throwing inside the
                 *     write lock.
                 */
                if (!base) { rangeResolved = false; return; }
                /**
                 * WHEN no bookmark target was supplied
                 *   CONSEQUENCE
                 *     Use the current selection as the apply range.
                 */
                range = insertAfter ? base.getRange(Word.RangeLocation.after) : base;
            } else {
                range = context.document.getSelection();
            }

            try {
                await applyOoxmlChanges(context, range, ooxml, insertAfter, applyContext, recorder, bookmarks, {
                    origin,
                    /**
                     * CASES
                     *   - numbered attempt   -> mark repair in progress
                     *   - `null`             -> repair finished; do not re-trigger progress
                     */
                    onRepairStatus: (attempt) => { if (attempt !== null) setInProgress(); },
                });
            } catch (e) {
                /**
                 * WHEN the locked apply ended with an `OoxmlMergeError`
                 *   CONSEQUENCE
                 *     Store it for recovery handling after the write lock is released.
                 */
                if (e instanceof OoxmlMergeError) { mergeFailure = e; }
                /**
                 * WHEN the failure is not an `OoxmlMergeError`
                 *   CONSEQUENCE
                 *     Re-throw immediately. It is not part of OOXML recovery flow.
                 */
                else { throw e; }
            }
        });

        /**
         * WHEN bookmark resolution produced no live target range
         *   CONSEQUENCE
         *     Record range-unresolved failure and return false after leaving the
         *     write lock.
         */
        if (!rangeResolved) {
            const { failure, error } = describeWhyTheRangeIsUnresolved(bookmarks ?? []);
            recorder.recordFailureAndKeepItsSnapshotId({ applyContext, failure, error });
            recordOutcomeOnce('range_unresolved');
            return false;
        }

        /**
         * WHEN an `OoxmlMergeError` survived the locked apply
         *   CONSEQUENCE
         *     Enter post-lock recovery handling.
         */
        if (mergeFailure) {
            /**
             * WHEN no agent fallback was provided
             *   CONSEQUENCE
             *     Treat the surviving merge failure as terminal.
             */
            if (!runAgentRecovery) {
                reportTerminal('failed');
                recordOutcomeOnce('hard_failure');
                throw new UserFacingError(
                    "Failed to apply changes to the document. The OOXML may be incompatible.",
                    mergeFailure,
                );
            }
            // Agent fallback runs outside the write lock; the agent's tools
            // re-enter the RW lock via execute_js / request_focused_document.
            setInProgress();
            try {
                const outcome = await runAgentRecovery({ error: mergeFailure, applyContext, recorder });
                /**
                 * CASES
                 *   - recovery status is `drift`  -> report `warning`
                 *   - recovery status is clean    -> report `success`
                 */
                reportTerminal(outcome.status === 'drift' ? 'warning' : 'success');
            const recoveryReportedDrift = outcome.status === 'drift';
            const recordedRecoveryOutcome = recoveryReportedDrift ? 'recovered_agent_drift' : 'recovered_agent_clean';
                /**
                 * CASES
                 *   - recovery status is `drift`  -> record `recovered_agent_drift`
                 *   - recovery status is clean    -> record `recovered_agent_clean`
                 */
                recordOutcomeOnce(
                    recordedRecoveryOutcome,
                );
            } catch (e) {
                reportTerminal('failed');
                recordOutcomeOnce('hard_failure');
                /**
                 * WHEN backend repair ran and no merge failure remains
                 *   CONSEQUENCE
                 *     Emit terminal success and record backend-repair recovery.
                 */
                throw e;
            }
        } else if (inProgress) {
            // Backend repair recovered the merge cleanly.
            reportTerminal('success');
            /**
             * WHEN neither backend repair nor agent recovery ever started
             *   CONSEQUENCE
             *     Record a clean apply.
             */
            recordOutcomeOnce('recovered_backend_repair');
        } else {
            recordOutcomeOnce('applied_clean');
        }

        return true;
    } catch (error) {
        recordOutcomeOnce('hard_failure');
        throw error;
    } finally {
        /**
         * WHEN some repair path started but no terminal status was sent
         *   CONSEQUENCE
         *     Report `failed` from `finally`.
         *
         * WHY
         *   Covers unexpected throws during recovery.
         */
        if (inProgress) { reportTerminal('failed'); }
    }
};

export const setPropertyInDocSettings = (keyEqVal: string, appendToExistingValue?: boolean) => withWordContextWrite(async (context) => {
    const settings = context.document.settings;

    const [propertyName, propertyValue] = keyEqVal.split("=");
    const existingSetting = settings.getItemOrNullObject(propertyName);
    existingSetting.load("value");

    await context.sync();

    /**
     * WHEN the setting key already exists
     *   CONSEQUENCE
     *     Update that existing setting in place.
     */
    if (!existingSetting.isNullObject) {
        /**
         * WHEN this setting stores a comma-separated list
         *   CONSEQUENCE
         *     Append the new item instead of overwriting the current value.
         */
        if (appendToExistingValue) {
            const currentValue = existingSetting.value;
            /**
             * WHEN this setting is single-value here
             *   CONSEQUENCE
             *     Replace its current value outright.
             */
            existingSetting.value = `${currentValue},${propertyValue}`;
        } else {
            /**
             * WHEN the setting key does not exist yet
             *   CONSEQUENCE
             *     Create it.
             */
            existingSetting.value = propertyValue;
        }
    } else {
        settings.add(propertyName, propertyValue);
    }
});

export const fetchPropertyFromDocSettings = (propertyName: string) => withWordContextRead(async (context) => {
    const settings = context.document.settings;
    const setting = settings.getItemOrNullObject(propertyName);
    setting.load("value");

    await context.sync();

    /**
     * WHEN the setting key has never been stored
     *   CONSEQUENCE
     *     Return null.
     */
    if (setting.isNullObject) {
        /**
         * WHEN the setting key exists
         *   CONSEQUENCE
         *     Return its stored value.
         */
        return null;
    } else {
        return setting.value;
    }
});

export const removePropertyFromDocSettings = async (propertyName: string, value: string): Promise<void> => {

    return withWordContextWrite(async (context) => {
        const settings = context.document.settings;
        const setting = settings.getItemOrNullObject(propertyName);
        setting.load("value");

        await context.sync();

        /**
         * WHEN the setting key exists
         *   CONSEQUENCE
         *     Remove the requested item from its comma-separated value list.
         *     If the key is missing, leave it alone.
         */
        if (!setting.isNullObject) {
            const existingValue = setting.value;
            const values = existingValue.split(",").map(v => v.trim());
            const filteredValues = values.filter(v => v !== value);

            /**
             * WHEN removing this item leaves no values
             *   CONSEQUENCE
             *     Delete the whole setting.
             */
            if (filteredValues.length === 0) {
                /**
                 * WHEN at least one value remains after removal
                 *   CONSEQUENCE
                 *     Write back the remaining comma-separated list.
                 */
                setting.delete();
            } else {
                setting.value = filteredValues.join(",");
            }
        }
    });
};

export const fetchCustomProperty = (propertyName: string) => withWordContextRead(async (context) => {
    const customProperties = context.document.properties.customProperties;
    const customProperty = customProperties.getItemOrNullObject(propertyName);
    customProperty.load("value");

    await context.sync();

    /**
     * WHEN the custom property is absent
     *   CONSEQUENCE
     *     Return null.
     */
    if (customProperty.isNullObject) {
        /**
         * WHEN the custom property exists
         *   CONSEQUENCE
         *     Return its stored value.
         */
        return null;
    } else {
        return customProperty.value;
    }
});

// Batch reader: load several custom properties in a single context.sync()
// (one round-trip, e.g. for WOPI_MODE + ICI_URL during WOPI bootstrap).
export const fetchAllCustomProperties = () =>
    withWordContextRead(async (context) => {
        const collection = context.document.properties.customProperties;
        collection.load("items/key,items/value");
        await context.sync();
        const result: Record<string, string> = {};
        collection.items.forEach((item) => {
            result[item.key] = String(item.value);
        });
        return result;
    });

/**
 * Strips binary data from OOXML to reduce size while maintaining valid XML structure.
 * Replaces pkg:binaryData elements and other large binary content with placeholder comments.
 * This ensures OOXML remains valid while significantly reducing size for text snippet creation.
 */
export const stripBinaryDataFromOoxml = (ooxml: string): string => {
    const ooxmlIsMissing = !ooxml;
    const ooxmlIsNotAString = typeof ooxml !== "string";
    if (ooxmlIsMissing || ooxmlIsNotAString) {
        return ooxml;
    }

    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(ooxml, "text/xml");

        // If parsing failed, return original to avoid corruption
        if (doc.getElementsByTagName("parsererror").length > 0) {
            return ooxml;
        }

        const forEachElement = (
            predicate: (el: Element) => boolean,
            fn: (el: Element) => void
        ) => {
            const walker = doc.createTreeWalker(doc, NodeFilter.SHOW_ELEMENT);
            let node = walker.nextNode();
            while (node) {
                const el = node as Element;
                /**
                 * WHEN the current element matches this stripping rule
                 *   CONSEQUENCE
                 *     Apply the mutation callback to that element only.
                 */
                if (predicate(el)) fn(el);
                node = walker.nextNode();
            }
        };

        // 1) Remove pkg:binaryData elements entirely, replace with an XML comment
        forEachElement(
            (el) => el.localName === "binaryData" && (el.namespaceURI || "").includes("xmlPackage"),
            (el) => {
                const comment = doc.createComment(" Binary data omitted ");
                el.parentNode?.replaceChild(comment, el);
            }
        );

        // 2) Redact inline base64 images in v:imagedata src attribute (never insert comments in attributes)
        forEachElement(
            (el) => el.localName === "imagedata" && (el.namespaceURI || "").includes("schemas-microsoft-com:vml"),
            (el) => {
                const src = el.getAttribute("src");
                const srcIsPresentAndInlineDataUrl = (): boolean => {
                    if (!src) return false;
                    return /^data:/i.test(src);
                };
                /**
                 * WHEN `src` is an inline data URL
                 *   CONSEQUENCE
                 *     Clear it.
                 *
                 * WHY
                 *   Redact embedded payloads without touching ordinary linked image sources.
                 */
                if (srcIsPresentAndInlineDataUrl()) {
                    el.setAttribute("src", "");
                }
            }
        );

        // 3) Remove w:font elements which may embed font data
        forEachElement(
            (el) => el.localName === "font" && (el.namespaceURI || "").includes("wordprocessingml/2006/main"),
            (el) => {
                el.parentNode?.removeChild(el);
            }
        );

        // 4) Redact long base64-looking content in TEXT NODES only (do not touch attributes)
        const base64Regex = /^[A-Za-z0-9+/]{100,}={0,2}$/;
        const textWalker = doc.createTreeWalker(doc, NodeFilter.SHOW_TEXT);
        let tNode = textWalker.nextNode();
        while (tNode) {
            const tn = tNode as Text;
            const trimmed = tn.nodeValue ? tn.nodeValue.trim() : "";
            const textIsLongEnoughToBeEmbeddedPayload = trimmed.length >= 100;
            const textLooksLikeBase64Payload = textIsLongEnoughToBeEmbeddedPayload && base64Regex.test(trimmed);
            /**
             * WHEN a text node is long enough and matches the base64 pattern
             *   CONSEQUENCE
             *     Replace its contents with a placeholder.
             *
             * WHY
             *   Avoid false positives on ordinary short text.
             */
            if (textLooksLikeBase64Payload) {
                tn.nodeValue = " Base64 content omitted ";
            }
            tNode = textWalker.nextNode();
        }

        return new XMLSerializer().serializeToString(doc);
    } catch (error) {
        console.warn("Error stripping binary data from OOXML:", error);
        return ooxml; // Return original if stripping fails
    }
};

export const getBookmarkedSectionsOoxml = async (bookmarkNames: string[]): Promise<string | null> => {

    return withWordContextRead(async (context) => {
        const expandedRange = await getExpandedRangeForBookmarks(context, bookmarkNames);

        /**
         * WHEN none of the requested bookmarks resolve to a live range
         *   CONSEQUENCE
         *     Return null.
         */
        if (!expandedRange) return null;

        const ooxml = expandedRange.getOoxml();
        await context.sync();

        // eslint-disable-next-line office-addins/load-object-before-read
        return ooxml.value;
    });
};

export const getOoxmlForBookmarks = async (bookmarkNames: string[]): Promise<string | null> => {
    return getBookmarkedSectionsOoxml(bookmarkNames);
};

export interface BulkApplyCommentEntry {
    target_bookmark_names: string[];
    comment_text: string;
    target_text: string;
    /**
     * Where the review placed the comment (`CommentPlacement`); null or absent when
     * the review did not say, and the comment covers the bookmarked range.
     */
    placement?: CommentPlacement | null;
    /** The words a "span" comment sits on; null or absent for every other placement. */
    anchor_text?: string | null;
    rule_uuids?: string[];
    rule_names?: string[];
    issue_comment_id?: string | null;
    waits_for_rule_uuid?: string | null;
}

export type BulkApplyMode = "batched" | "individual";

export interface BulkApplyProgress {
    processed: number;
    total: number;
    inFlight: number;
    mode: BulkApplyMode;
}

/**
 * Where a package to write came from, which decides what production machinery
 * runs around the write.
 *
 * CASES
 *   - "pipeline" -> the server pipeline's markup: the untracked-write guard before
 *                   the insert, the backend OOXML fixer and the agent fallback after a
 *                   refusal, as production has always done
 *   - "engine"   -> a package the add-in's own engine composed: none of that runs.
 *                   The engine writes tracked changes by construction, and a model
 *                   rewriting a 170k-character table package is never the answer
 *                   for engine output (2026-09-22: the guard snapshotted two engine
 *                   table packages and two repairs ran two minutes each and gave up).
 *                   When Word refuses the insert, the entry fails at once with
 *                   Word's reason.
 */
export type PackageOrigin = "pipeline" | "engine";

export interface ApplyBulkMergeOptions {
    origin: PackageOrigin;
    /** Aggregate repair indicator: `true` while any entry is currently
     *  repairing, `false` otherwise. Bulk apply has no per-entry warning
     *  slot in the UI, so warnings are not surfaced here. */
    onRepairing?: (inProgress: boolean) => void;
    onProgress?: (progress: BulkApplyProgress) => void;
    /** Threaded in by `useApplyBulkMergeData`. */
    runAgentRecovery?: (args: RunAgentRecoveryArgs) => Promise<RecoveryOutcome>;
}

const dedupeValidMergeEntries = (
    mergeData: BulkApplyMergeEntry[],
    failedRuleIds: Set<string>,
    dropCounts: Map<RedlineDropReason, number>,
    sendError?: (error: any, context?: any) => void,
): BulkApplyMergeEntry[] => {
    const seenEntries = new Set<string>();
    const validEntries: BulkApplyMergeEntry[] = [];
    const countDrop = (reason: RedlineDropReason) =>
        dropCounts.set(reason, (dropCounts.get(reason) ?? 0) + 1);

    for (const changeEntry of mergeData) {
        const { target_bookmark_names, target_ooxml_after } = changeEntry;
        const entryHasNoWritableTarget = (): boolean => {
            const targetBookmarkNamesAreMissing = !target_bookmark_names;
            if (targetBookmarkNamesAreMissing) return true;
            const targetOoxmlAfterIsMissing = !target_ooxml_after;
            if (targetOoxmlAfterIsMissing) return true;
            const targetBookmarkNamesAreEmpty = target_bookmark_names.length === 0;
            if (targetBookmarkNamesAreEmpty) return true;
            return !target_ooxml_after[0];
        };

        /**
         * WHEN a bulk merge entry has no usable bookmark list or no first OOXML payload
         *   CONSEQUENCE
         *     Drop this entry and count why it was dropped.
         */
        if (entryHasNoWritableTarget()) {
            const entryIsMissingTargetFields = (): boolean => {
                const targetBookmarkNamesAreMissing = !target_bookmark_names;
                if (targetBookmarkNamesAreMissing) return true;
                const targetOoxmlAfterIsMissing = !target_ooxml_after;
                if (targetOoxmlAfterIsMissing) return true;
                const targetBookmarkNamesAreEmpty = target_bookmark_names.length === 0;
                if (targetBookmarkNamesAreEmpty) return true;
                return target_ooxml_after.length === 0;
            };
            countDrop(
                entryIsMissingTargetFields()
                    ? "missing_target_fields"
                    : "empty_ooxml",
            );
            const failedRules = failEveryRuleOfEntry(changeEntry, failedRuleIds);
            sendError?.(new Error(UNWRITABLE_MERGE_ENTRY_MESSAGE), {
                component: "applyBulkMergeData",
                action: "apply_merge",
                rules: failedRules,
                bookmarks: target_bookmark_names ?? [],
            });
            /**
             * WHEN the entry was dropped as invalid
             *   CONSEQUENCE
             *     Skip it so later batching sees only writable merges.
             */
            continue;
        }

        const changeKey = `${target_bookmark_names.join(',')}_${target_ooxml_after.join('')}`;
        /**
         * WHEN this bookmark set + OOXML payload pair was already seen
         *   CONSEQUENCE
         *     Drop the duplicate entry.
         */
        if (seenEntries.has(changeKey)) {
            countDrop("duplicate_change_key");
            /**
             * WHEN this entry is a duplicate
             *   CONSEQUENCE
             *     Skip it and keep only the first instance.
             */
            continue;
        }
        seenEntries.add(changeKey);

        validEntries.push(changeEntry);
    }

    return validEntries;
};

const failEveryRuleOfEntry = (
    changeEntry: { rule_uuids?: string[]; rule_names?: string[] },
    failedRuleIds: Set<string>,
) =>
    (changeEntry.rule_uuids ?? []).map((uuid, index) => {
        failedRuleIds.add(uuid);
        return { rule_id: uuid, rule_name: changeEntry.rule_names?.[index] };
    });

const snapshotTheBatchEntryWhoseBookmarksVanished = (
    changeEntry: BulkApplyMergeEntry,
    applySource: RedlineApplySource,
): RedlineAttemptRecorder => {
    const recorder = createRedlineAttemptRecorder(applySource);
    recorder.recordFailureAndKeepItsSnapshotId({
        applyContext: startApplyAttempt({
            capture: { status: "not_captured" },
            reviewId: readReviewIdOfApplySource(applySource),
        }),
        failure: {
            stage: "range_unresolved",
            bookmarks: changeEntry.target_bookmark_names,
            /**
             * EXAMPLE
             *   target_ooxml_after = ["<pkg:package...>"]
             *   [0]                -> first prepared payload, recorded as the attempted write
             */
            attemptedOoxml: changeEntry.target_ooxml_after[0],
        },
        error: new Error(MISSING_BOOKMARKS_MESSAGE),
    });
    recorder.recordAttemptEndedAs("range_unresolved");
    return recorder;
};

const applySingleMergeEntry = async (
    changeEntry: BulkApplyMergeEntry,
    applySource: RedlineApplySource,
    failedRuleIds: Set<string>,
    sendError?: (error: any, context?: any) => void,
    repairOptions?: ApplyBulkMergeOptions,
): Promise<boolean> => {
    try {
        const applied = await applyRedlineOoxml({
            /**
             * EXAMPLE
             *   target_ooxml_after = ["<pkg:package...>"]
             *   [0]                -> payload applied for this merge entry
             */
            ooxml: changeEntry.target_ooxml_after[0],
            applySource,
            bookmarks: changeEntry.target_bookmark_names,
            insertAfter: false,
            origin: repairOptions?.origin ?? "pipeline",
            /** The agent fallback is the pipeline path's second stage; an engine package gets no fallback either. */
            runAgentRecovery: repairOptions?.origin === "engine" ? undefined : repairOptions?.runAgentRecovery,
            onStatus: repairOptions?.onRepairing
                ? (status) => repairOptions.onRepairing!(status === 'in_progress')
                : undefined,
        });
        /**
         * WHEN `applyRedlineOoxml` returned false
         *   CONSEQUENCE
         *     Treat it as bookmark drift: no live target range was resolved.
         */
        if (!applied) {
            failEveryRuleOfEntry(changeEntry, failedRuleIds);
        }
        return applied;
    } catch (error) {
        console.error(`Failed to apply changes to bookmarks ${changeEntry.target_bookmark_names.join(', ')}:`, error);

        /**
         * WHEN apply logic already produced a `UserFacingError`
         *   CONSEQUENCE
         *     Show that exact user-facing message.
         */
        if (error instanceof UserFacingError) {
            /**
             * WHEN the failure is not already classified for the UI
             *   CONSEQUENCE
             *     Show the generic fallback toast.
             */
            toastError(error);
        } else {
            toastError("We had an error applying the changes. Skipping ...");
        }

        const failedRules = failEveryRuleOfEntry(changeEntry, failedRuleIds);
        sendError?.(error, {
            component: "applyBulkMergeData",
            action: "apply_merge",
            rules: failedRules,
            bookmarks: changeEntry.target_bookmark_names
        });
        return false;
    }
};

const unlockPreparedRangesBestEffort = async (
    context: Word.RequestContext,
    prepared: Array<{ controls: Word.ContentControlCollection; parent: Word.ContentControl }>,
): Promise<void> => {
    try {
        prepared.forEach(({ controls }) => controls.items.forEach((control) => {
            control.cannotEdit = false;
            control.cannotDelete = false;
        }));

        let parents = prepared.map(({ parent }) => parent).filter((parent) => !parent.isNullObject);
        while (parents.length > 0) {
            const ancestors: Word.ContentControl[] = [];
            for (const parent of parents) {
                parent.cannotEdit = false;
                parent.cannotDelete = false;
                const ancestor = parent.parentContentControlOrNullObject;
                ancestor.load("isNullObject, cannotEdit, cannotDelete");
                ancestors.push(ancestor);
            }
            await context.sync();
            parents = ancestors.filter((parent) => !parent.isNullObject);
        }
    } catch (error) {
        console.warn("Unlocking content controls failed, continuing with batch apply:", error);
    }
};

interface MergeBatchOutcome {
    missingBookmarkEntries: BulkApplyMergeEntry[];
    localMergeFailedEntries: BulkApplyMergeEntry[];
}

const applyMergeBatch = async (batch: BulkApplyMergeEntry[], bodyRead: BulkApplyBodyRead): Promise<MergeBatchOutcome> => {
    const missingBookmarkEntries: BulkApplyMergeEntry[] = [];
    const localMergeFailedEntries: BulkApplyMergeEntry[] = [];
    const lockRequestedAtMs = bulkApplyClockMs();

    await withWordContextWrite(async (context) => {
        recordBulkApplySpan("batch_word_lock_wait", lockRequestedAtMs);
        const bookmarksResolveStartedAtMs = bulkApplyClockMs();
        context.document.load("changeTrackingMode");
        const resolutions = batch.map((entry) => {
            const invalidNames = entry.target_bookmark_names.filter((name) => !isValidWordBookmarkName(name));
            /**
             * WHEN this batch entry includes invalid bookmark names
             *   CONSEQUENCE
             *     Report them, but still try any remaining valid names.
             */
            if (invalidNames.length > 0) {
                reportInvalidBookmarkNames(entry.target_bookmark_names, invalidNames);
            }
            const bookmarkRanges = entry.target_bookmark_names
                .filter(isValidWordBookmarkName)
                .map((name) => context.document.getBookmarkRangeOrNullObject(name));
            bookmarkRanges.forEach((range) => range.load("isNullObject"));
            return { entry, bookmarkRanges };
        });
        await context.sync();
        recordBulkApplySpan("batch_bookmarks_resolved", bookmarksResolveStartedAtMs, { entries: batch.length });

        const originalTrackingMode = context.document.changeTrackingMode;
        /**
         * WHEN the document was not already in tracking-off mode
         *   CONSEQUENCE
         *     Turn tracking off for the batch, so it can be restored later.
         */
        if (originalTrackingMode !== Word.ChangeTrackingMode.off) {
            context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
        }

        const inserts: QueuedMergeInsert[] = [];

        try {
            const targetsPrepareStartedAtMs = bulkApplyClockMs();
            const pending: Array<{
                entry: BulkApplyMergeEntry;
                baseRange: Word.Range;
                paragraphs: Word.ParagraphCollection;
            }> = [];
            for (const { entry, bookmarkRanges } of resolutions) {
                const liveRanges = bookmarkRanges.filter((range) => !range.isNullObject);
                /**
                 * WHEN every bookmark in this entry resolved to a null object
                 *   CONSEQUENCE
                 *     Mark the entry as missing and skip merge preparation.
                 */
                if (liveRanges.length === 0) {
                    missingBookmarkEntries.push(entry);
                    continue;
                }
                /**
                 * EXAMPLE
                 *   liveRanges.length = 3
                 *   liveRanges[0]     -> first surviving range
                 *   later iterations  -> expand to include liveRanges[1] and liveRanges[2]
                 */
                let baseRange = liveRanges[0].getRange();
                for (let i = 1; i < liveRanges.length; i++) {
                    const nextLiveRange = liveRanges[i].getRange();
                    baseRange = baseRange.expandTo(nextLiveRange);
                }
                // eslint-disable-next-line office-addins/call-sync-before-read
                const paragraphs = baseRange.paragraphs;
                paragraphs.load("items");
                pending.push({ entry, baseRange, paragraphs });
            }
            await context.sync();

            const prepared: Array<{
                entry: BulkApplyMergeEntry;
                range: Word.Range;
                controls: Word.ContentControlCollection;
                parent: Word.ContentControl;
                tables: Word.TableCollection;
            }> = [];
            for (const { entry, baseRange, paragraphs } of pending) {
                let range = baseRange;
                /**
                 * WHEN the resolved bookmark union has enclosing paragraphs
                 *   CONSEQUENCE
                 *     Expand the target to whole paragraphs before replacement.
                 */
                if (paragraphs.items.length > 0) {
                    /**
                     * EXAMPLE
                     *   paragraphs.items.length = 2
                     *   items[0]                -> first enclosing paragraph range
                     *   later iterations        -> expand to include items[1]
                     */
                    range = paragraphs.items[0].getRange();
                    for (let i = 1; i < paragraphs.items.length; i++) {
                        const nextParagraphRange = paragraphs.items[i].getRange();
                        range = range.expandTo(nextParagraphRange);
                    }
                }
                const controls = range.contentControls;
                controls.load("cannotEdit, cannotDelete");
                const parent = range.parentContentControlOrNullObject;
                parent.load("isNullObject, cannotEdit, cannotDelete");
                range.load("text");
                const tables = range.tables;
                tables.load("items");
                prepared.push({ entry, range, controls, parent, tables });
            }
            await context.sync();

            await unlockPreparedRangesBestEffort(context, prepared);
            recordBulkApplySpan("batch_targets_prepared", targetsPrepareStartedAtMs, { entries: prepared.length });

            for (const item of prepared) {
                /**
                 * WHEN the prepared target range is empty
                 *   CONSEQUENCE
                 *     Insert a space first so replace semantics operate on real paragraph
                 *     content.
                 */
                if ((item.range.text?.length || 0) === 0) {
                    item.range.insertText(" ", Word.InsertLocation.start);
                }
            }
            const currentOoxmls = await timedBulkApplySpan("batch_current_read", () => readCurrentOoxmlsOfBatchEntries(
                context,
                prepared.map(({ entry, range, tables }) => ({
                    bookmarkNames: entry.target_bookmark_names.filter(isValidWordBookmarkName),
                    range,
                    readFromWordOnly: (range.text?.length || 0) === 0 || tables.items.length > 0,
                })),
                maxBatchPayloadChars(),
                (documentPages) => bodyRead.startFromBodyExport(documentPages),
            ), { entries: prepared.length });

            const packagesMergeStartedAtMs = bulkApplyClockMs();
            prepared.forEach((item, index) => {
                const currentOoxml = currentOoxmls[index];
                try {
                const unlockedTargetOoxml = stripSdtLocks(item.entry.target_ooxml_after[0]);
                    /**
                     * EXAMPLE
                     *   item.entry.target_ooxml_after = ["<pkg:package...>"]
                     *   [0]                           -> first prepared payload merged into
                     *                                    `currentOoxml`
                     */
                    const mergedOoxml = mergeOoxmlPackages(currentOoxml, unlockedTargetOoxml);
                    inserts.push({ entry: item.entry, range: item.range, tables: item.tables, mergedOoxml, originalOoxml: currentOoxml });
                } catch (error) {
                    console.warn(`Pre-merge failed for bookmarks ${item.entry.target_bookmark_names.join(', ')}, retrying individually:`, error);
                    localMergeFailedEntries.push(item.entry);
                }
            });
            recordBulkApplySpan("batch_packages_merged", packagesMergeStartedAtMs, { entries: prepared.length });

            const insertsWriteStartedAtMs = bulkApplyClockMs();
            for (const { range, tables, mergedOoxml } of inserts) {
                tables.items.forEach((table) => table.delete());
                range.insertOoxml(mergedOoxml, Word.InsertLocation.replace);
            }
            /**
             * WHEN the batch changed tracking mode earlier
             *   CONSEQUENCE
             *     Restore the original mode before the successful sync.
             */
            if (originalTrackingMode !== Word.ChangeTrackingMode.off) {
                context.document.changeTrackingMode = originalTrackingMode;
            }
            await context.sync();
            recordBulkApplySpan("batch_inserts_written", insertsWriteStartedAtMs, { entries: inserts.length });
            bodyRead.recordMergeWrittenPackages(inserts.map(({ originalOoxml, mergedOoxml }) => ({ replacedPackageOoxml: originalOoxml, mergedPackageOoxml: mergedOoxml })));
        } catch (error) {
            batch.forEach((entry) => bodyRead.recordWritesWithoutPackage(entry.target_bookmark_names));
            /**
             * WHEN the error path follows a temporary tracking-mode change
             *   CONSEQUENCE
             *     Best-effort restore the original tracking mode.
             */
            if (originalTrackingMode !== Word.ChangeTrackingMode.off) {
                try {
                    context.document.changeTrackingMode = originalTrackingMode;
                    await context.sync();
                } catch (restoreError) {
                    reportError(restoreError, { extra: { component: "applyBulkMergeData", action: "restore_tracking_mode_after_failed_insert" } });
                }
            }
            const landedEntries = await mergeEntriesWhoseWriteLanded(context, inserts);
            throw new MergeBatchFailure(batch.filter((entry) => !landedEntries.has(entry)), error);
        }
    });

    return { missingBookmarkEntries, localMergeFailedEntries };
};

interface QueuedMergeInsert {
    entry: BulkApplyMergeEntry;
    range: Word.Range;
    tables: Word.TableCollection;
    mergedOoxml: string;
    originalOoxml: string;
}

class MergeBatchFailure extends Error {
    constructor(readonly entriesLeftUnwritten: BulkApplyMergeEntry[], readonly cause: unknown) {
        super(cause instanceof Error ? cause.message : String(cause));
        this.name = "MergeBatchFailure";
    }
}

async function mergeEntriesWhoseWriteLanded(context: Word.RequestContext, inserts: QueuedMergeInsert[]): Promise<Set<BulkApplyMergeEntry>> {
    const landedEntries = new Set<BulkApplyMergeEntry>();
    if (inserts.length === 0) return landedEntries;
    try {
        const queuedBookmarkRanges = inserts.map((insert) => queueBookmarkRanges(context, insert.entry.target_bookmark_names));
        await context.sync();
        const paragraphsOfEntries = queuedBookmarkRanges.map((bookmarkRanges) => {
            const bookmarkedRange = expandQueuedBookmarkRanges(bookmarkRanges);
            if (bookmarkedRange === null) return null;
            const paragraphs = bookmarkedRange.paragraphs;
            paragraphs.load("items");
            return paragraphs;
        });
        await context.sync();
        const reads = paragraphsOfEntries.map((paragraphs) => {
            if (paragraphs === null || paragraphs.items.length === 0) return null;
            const wholeParagraphs = paragraphs.items[0].getRange().expandTo(paragraphs.items[paragraphs.items.length - 1].getRange());
            return wholeParagraphs.getOoxml();
        });
        await context.sync();
        inserts.forEach((insert, insertIndex) => {
            const read = reads[insertIndex];
            if (read === null) return;
            const acceptedTextNow = new OoxmlRange(read.value).allChangesAcceptedTexts().join("\n");
            const acceptedTextBeforeTheWrite = new OoxmlRange(insert.originalOoxml).allChangesAcceptedTexts().join("\n");
            if (acceptedTextNow !== acceptedTextBeforeTheWrite) landedEntries.add(insert.entry);
        });
    } catch (readError) {
        reportError(readError, { extra: { component: "applyBulkMergeData", action: "read_writes_landed_before_failed_insert" } });
    }
    return landedEntries;
}

export interface ApplyBulkMergeDataArgs {
    mergeData: BulkApplyMergeEntry[];
    applySource: RedlineApplySource;
    sendError?: (error: any, context?: any) => void;
    repairOptions?: ApplyBulkMergeOptions;
}

export interface ApplyBulkMergeDataResult {
    failedRuleIds: string[];
    selected: number;
    attempted: number;
    drops: RedlineApplyDropCount[];
    mergeWrite: BulkMergeWriteSummary;
    bodyRead: BulkApplyBodyRead | null;
}

export const applyBulkMergeData = async ({
    mergeData,
    applySource,
    sendError,
    repairOptions,
}: ApplyBulkMergeDataArgs): Promise<ApplyBulkMergeDataResult> => {
    const mergeDataIsMissingOrEmpty = (): boolean => {
        const mergeDataIsMissing = !mergeData;
        if (mergeDataIsMissing) return true;
        return mergeData.length === 0;
    };
    /**
     * WHEN the caller supplied no merge entries
     *   CONSEQUENCE
     *     Return immediately with zero selected, attempted, and drops.
     */
    if (mergeDataIsMissingOrEmpty()) {
        return { failedRuleIds: [], selected: 0, attempted: 0, drops: [], mergeWrite: NO_MERGE_WRITE, bodyRead: null };
    }

    const failedRuleIds = new Set<string>();
    const bodyRead = new BulkApplyBodyRead();
    const dropCounts = new Map<RedlineDropReason, number>();
    const { entries, batches } = timedBulkApplySpanSync("merge_entries_packed", () => {
        const validEntries = dedupeValidMergeEntries(mergeData, failedRuleIds, dropCounts, sendError);
        return { entries: validEntries, batches: packMergeEntriesIntoBatches(validEntries, maxBatchPayloadChars()) };
    }, { entries: mergeData.length });
    const batchFailureRecorders: RedlineAttemptRecorder[] = [];
    const selected = mergeData.length;
    const attempted = entries.length;
    let processedEntryCount = 0;
    let entriesBatched = 0;
    let entriesIndividual = 0;
    const writeStartedAtMs = performance.now();

    const reportProgress = (inFlight: number, mode: BulkApplyMode) =>
        repairOptions?.onProgress?.({ processed: processedEntryCount, total: entries.length, inFlight, mode });

    try {
    for (const batch of batches) {
        let entriesToApplyIndividually = batch;
        let entriesWhoseBookmarksVanished = 0;

        /**
         * EXAMPLE
         *   batch.length = 3
         *   each entry   -> inspect `entry.target_ooxml_after[0]`
         *   batchable    -> true only if all three first payloads include `pkg:package`
         */
        const isBatchable = batch.length > 1 && batch.every((entry) => entry.target_ooxml_after[0].includes("pkg:package"));
        /**
         * WHEN the batch has multiple entries and every first payload is package OOXML
         *   CONSEQUENCE
         *     Use the faster shared-write batch path.
         */
        if (isBatchable) {
            try {
                reportProgress(batch.length, "batched");
                const outcome = await timedBulkApplySpan("merge_batch", () => applyMergeBatch(batch, bodyRead), { entries: batch.length });
                outcome.missingBookmarkEntries.forEach((entry) => {
                    failEveryRuleOfEntry(entry, failedRuleIds);
                    batchFailureRecorders.push(snapshotTheBatchEntryWhoseBookmarksVanished(entry, applySource));
                });
                entriesWhoseBookmarksVanished = outcome.missingBookmarkEntries.length;
                entriesToApplyIndividually = outcome.localMergeFailedEntries;
                processedEntryCount += batch.length - outcome.localMergeFailedEntries.length;
                reportProgress(batch.length - outcome.localMergeFailedEntries.length, "batched");
            } catch (error) {
                const entriesLeftUnwritten = error instanceof MergeBatchFailure ? error.entriesLeftUnwritten : batch;
                console.error(`Batch apply failed for ${batch.length} entries, retrying ${entriesLeftUnwritten.length} individually:`, error);
                sendError?.(error instanceof MergeBatchFailure ? error.cause : error, {
                    component: "applyBulkMergeData",
                    action: "apply_merge_batch",
                    batchSize: batch.length,
                    landedBeforeTheFailure: batch.length - entriesLeftUnwritten.length,
                });
                entriesToApplyIndividually = entriesLeftUnwritten;
                processedEntryCount += batch.length - entriesLeftUnwritten.length;
            }
        }

        entriesBatched += batch.length - entriesToApplyIndividually.length - entriesWhoseBookmarksVanished;
        entriesToApplyIndividually.forEach((entry) => bodyRead.recordWritesWithoutPackage(entry.target_bookmark_names));
        for (const changeEntry of entriesToApplyIndividually) {
            const writeLanded = await timedBulkApplySpan("merge_single_entry", () => applySingleMergeEntry(changeEntry, applySource, failedRuleIds, sendError, repairOptions));
            if (writeLanded) entriesIndividual += 1;
            processedEntryCount += 1;
            reportProgress(1, "individual");
        }
    }
    } finally {
        repairOptions?.onRepairing?.(false);
    }

    const drops: RedlineApplyDropCount[] = Array.from(dropCounts, ([reason, count]) => ({ reason, count }));
    await timedBulkApplySpan("merge_outcomes_recorded", async () => {
        await recordHowManyRedlinesTheGestureSelected({ source: applySource, selected, drops });
        const batchFailureRecordingSettlements = batchFailureRecorders.map((each) => each.whenEveryRecordingHasSettled());
        await Promise.all(batchFailureRecordingSettlements);
    });

    const payloadCharsOfEntries = entries.map(entryPayloadChars);
    const mergeWrite: BulkMergeWriteSummary = {
        entriesBatched,
        entriesIndividual,
        batchCount: batches.length,
        largestBatchEntries: Math.max(0, ...batches.map((batch) => batch.length)),
        payloadCharsTotal: payloadCharsOfEntries.reduce((total, chars) => total + chars, 0),
        payloadCharsLargestEntry: Math.max(0, ...payloadCharsOfEntries),
        batchPayloadCapChars: maxBatchPayloadChars(),
        writeDurationMs: Math.round(performance.now() - writeStartedAtMs),
    };
    return { failedRuleIds: Array.from(failedRuleIds), selected, attempted, drops, mergeWrite, bodyRead: bodyRead.holdsBodyExport ? bodyRead : null };
};

export interface CommentEntryFailureCause {
    failureReason: ApplyFailureReason;
    failureMessage: string;
}

export interface CommentEntryFailure extends CommentEntryFailureCause {
    commentEntry: BulkApplyCommentEntry;
}

export interface CommentEntryRelocation extends CommentRelocation {
    commentEntry: BulkApplyCommentEntry;
}

export interface BulkCommentWriteResult {
    failedRuleIds: string[];
    commentEntryFailures: CommentEntryFailure[];
    commentEntryRelocations: CommentEntryRelocation[];
}

export interface BulkCommentWriteOptions {
    onProgress?: (progress: BulkApplyProgress) => void;
    triesEveryComment?: boolean;
    bodyReadOfTheMerges?: BulkApplyBodyRead | null;
}

type FailureCauseByCommentEntry = Map<BulkApplyCommentEntry, CommentEntryFailureCause>;

type RelocationByCommentEntry = Map<BulkApplyCommentEntry, CommentRelocation>;

const COMMENT_PLACE_NOT_FOUND: CommentEntryFailureCause = { failureReason: APPLY_FAILURE_LOCATION_NOT_FOUND, failureMessage: CANNOT_LOCATE_TARGET_MESSAGE };

const failCommentEntry = (
    commentEntry: BulkApplyCommentEntry,
    failureCause: CommentEntryFailureCause,
    failureCauseByCommentEntry: FailureCauseByCommentEntry,
) => {
    failureCauseByCommentEntry.set(commentEntry, failureCause);
    return (commentEntry.rule_uuids ?? []).map((uuid, index) => ({ rule_id: uuid, rule_name: commentEntry.rule_names?.[index] }));
};

const recordMissingCommentBookmarks = (
    commentEntry: BulkApplyCommentEntry,
    failureCauseByCommentEntry: FailureCauseByCommentEntry,
    sendError?: (error: any, context?: any) => void,
) => {
    const { target_bookmark_names } = commentEntry;
    console.warn(`Could not find any of the bookmarks: ${target_bookmark_names.join(', ')}`);
    const failedRules = failCommentEntry(commentEntry, COMMENT_PLACE_NOT_FOUND, failureCauseByCommentEntry);
    sendError?.(new Error(MISSING_BOOKMARKS_MESSAGE), {
        component: "applyBulkCommentData",
        action: "apply_comment",
        fingerprint: redlineFailureFingerprint("range_unresolved"),
        rules: failedRules,
        bookmarks: target_bookmark_names
    });
};

const applyCommentBatch = async (
    context: Word.RequestContext,
    entries: BulkApplyCommentEntry[],
    relocationByCommentEntry: RelocationByCommentEntry,
    bodyReadOfTheMerges: BulkApplyBodyRead | null,
): Promise<{ missingBookmarkEntries: BulkApplyCommentEntry[] }> => {
    const bookmarksStartedAtMs = bulkApplyClockMs();
    const queued = entries.map((entry) => queueBookmarkRanges(context, entry.target_bookmark_names));
    await context.sync();
    recordBulkApplySpan("comment_bookmarks_resolved", bookmarksStartedAtMs, { entries: entries.length });

    const resolved: { entry: BulkApplyCommentEntry; range: Word.Range }[] = [];
    const missingBookmarkEntries: BulkApplyCommentEntry[] = [];
    entries.forEach((entry, index) => {
        /**
         * CASES
         *   - queued[index] has ranges  -> expand them into one combined range
         *   - queued[index] is empty    -> treat this entry as missing now
         */
        const range = queued[index].length ? expandQueuedBookmarkRanges(queued[index]) : null;
        /**
         * WHEN the comment entry resolved to a live Word range
         *   CONSEQUENCE
         *     Keep it for duplicate-comment detection and insertion.
         */
        if (range) {
            /**
             * WHEN the comment entry resolved to no live range
             *   CONSEQUENCE
             *     Collect it as a missing-bookmark entry.
             */
            resolved.push({ entry, range });
        } else {
            missingBookmarkEntries.push(entry);
        }
    });
    /**
     * WHEN every queued comment entry lost its bookmark anchor
     *   CONSEQUENCE
     *     Return early. There are no live ranges to inspect for existing comments.
     */
    if (resolved.length === 0) return { missingBookmarkEntries };

    /**
     * WHEN the review said where each comment sits
     *   CONSEQUENCE
     *     the duplicate check and the insertion both work on that narrower range, so a
     *     first-word comment is not mistaken for one covering the whole paragraph. The
     *     placements of the whole batch resolve together, in four syncs and one more per
     *     window of struck words a first word sits behind,
     *     rather than one to five syncs per comment.
     */
    const targetsStartedAtMs = bulkApplyClockMs();
    const targets = await commentTargets(resolved.map(({ entry, range }) => ({
        bookmarkedRange: range,
        bookmarkNames: entry.target_bookmark_names,
        placement: entry.placement ?? null,
        anchorText: entry.anchor_text ?? null,
    })), bodyReadOfTheMerges);
    recordBulkApplySpan("comment_targets_resolved", targetsStartedAtMs, {
        entries: resolved.length,
        relocated: targets.filter((target) => target.landing === "relocated").length,
    });

    const existingStartedAtMs = bulkApplyClockMs();
    const existingComments = targets.map((target) => {
        const comments = target.range.getComments();
        context.load(comments, "items/content");
        return comments;
    });
    targets.forEach((target) => {
        if (target.landing === "relocated") target.range.load("text");
    });
    await context.sync();
    recordBulkApplySpan("comment_existing_read", existingStartedAtMs, {
        entries: targets.length,
        existingComments: existingComments.reduce((total, comments) => total + comments.items.length, 0),
    });

    const relationsStartedAtMs = bulkApplyClockMs();
    const anchorRelations = resolved.map(({ entry }, index) =>
        existingComments[index].items
            .filter((comment) => comment.content?.trim() === entry.comment_text.trim())
            .map((comment) => comment.getRange().compareLocationWith(targets[index].range)),
    );
    await context.sync();
    recordBulkApplySpan("comment_anchor_relations", relationsStartedAtMs, {
        comparisons: anchorRelations.reduce((total, relations) => total + relations.length, 0),
    });

    const insertsStartedAtMs = bulkApplyClockMs();
    let commentsInserted = 0;
    const relocationsWritten: [BulkApplyCommentEntry, CommentRelocation][] = [];
    resolved.forEach(({ entry }, index) => {
        const alreadyAnchored = anchorRelations[index].some(
            (relation) =>
                relation.value === Word.LocationRelation.equal ||
                relation.value === Word.LocationRelation.contains ||
                relation.value === Word.LocationRelation.inside,
        );
        /**
         * WHEN an identical comment is already anchored on this range, around it, or inside it
         *   CONSEQUENCE
         *     Skip insertion to avoid a duplicate thread. An earlier apply that placed the
         *     comment on the whole paragraph compares as `contains` against today's
         *     span target, and one placed on the span compares as `inside` against a
         *     whole-paragraph target; both are the same comment already in the margin.
         */
        if (alreadyAnchored) return;
        const target = targets[index];
        target.range.insertComment(entry.comment_text);
        commentsInserted += 1;
        if (target.landing === "relocated") relocationsWritten.push([entry, { relocationReason: target.relocationReason, landedOnText: target.range.text }]);
    });
    await context.sync();
    recordBulkApplySpan("comment_inserts_written", insertsStartedAtMs, {
        inserted: commentsInserted,
        skippedAsExisting: resolved.length - commentsInserted,
    });
    relocationsWritten.forEach(([entry, relocation]) => relocationByCommentEntry.set(entry, relocation));

    return { missingBookmarkEntries };
};

const relocationOfWrittenComment = async (context: Word.RequestContext, target: CommentTarget): Promise<CommentRelocation | null> => {
    if (target.landing === "as_placed") return null;
    target.range.load("text");
    await context.sync();
    return { relocationReason: target.relocationReason, landedOnText: target.range.text };
};

export const writeReviewComment = async (
    context: Word.RequestContext,
    request: CommentTargetRequest,
    commentText: string,
): Promise<CommentRelocation | null> => {
    const target = await commentTarget(request);
    try {
        await withTemporaryTrackingMode(context, Word.ChangeTrackingMode.off, async () => {
            await applyCommentChange(target.range, commentText);
        });
    } catch (error) {
        if (!isWordCommentInsertRefusal(error)) throw error;
        const landing = await retryRefusedCommentInsert(context, request, commentText, error);
        if (landing.route === "paragraph_content") return relocationOfWrittenComment(context, { range: landing.landedOn, landing: "relocated", relocationReason: "word_refused_placement" });
    }
    return relocationOfWrittenComment(context, target);
};

type SingleCommentApplyOutcome = "settled" | "failed" | "refused_by_word";

const applySingleCommentEntry = async (
    commentEntry: BulkApplyCommentEntry,
    failureCauseByCommentEntry: FailureCauseByCommentEntry,
    relocationByCommentEntry: RelocationByCommentEntry,
    sendError?: (error: any, context?: any) => void,
): Promise<SingleCommentApplyOutcome> => {
    const { target_bookmark_names, comment_text } = commentEntry;
    try {
        await withWordContextWrite(async (context) => {
            const range = await getExpandedRangeForBookmarks(context, target_bookmark_names);
            /**
             * WHEN the comment target bookmarks resolved to a live range
             *   CONSEQUENCE
             *     Apply the comment where the review placed it within that range.
             */
            if (range) {
                const relocation = await writeReviewComment(context, {
                    bookmarkedRange: range,
                    placement: commentEntry.placement ?? null,
                    anchorText: commentEntry.anchor_text ?? null,
                }, comment_text);
                if (relocation !== null) relocationByCommentEntry.set(commentEntry, relocation);
            } else {
                recordMissingCommentBookmarks(commentEntry, failureCauseByCommentEntry, sendError);
            }
        });
        return "settled";
    } catch (error) {
        console.error(`Failed to apply comment to bookmarks ${target_bookmark_names.join(', ')}:`, error);
        const failedRules = failCommentEntry(commentEntry, { failureReason: applyFailureReason(error), failureMessage: userMessageOf(error) }, failureCauseByCommentEntry);
        sendError?.(error, {
            component: "applyBulkCommentData",
            action: "apply_comment",
            rules: failedRules,
            bookmarks: target_bookmark_names
        });
        return isWordCommentInsertRefusal(error) ? "refused_by_word" : "failed";
    }
};

const skipCommentsAfterWordRefusal = (
    remainingCommentEntries: BulkApplyCommentEntry[],
    failureCauseByCommentEntry: FailureCauseByCommentEntry,
): void => {
    remainingCommentEntries.forEach((commentEntry) => failCommentEntry(commentEntry, { failureReason: APPLY_FAILURE_INSERT_FAILED, failureMessage: COMMENTS_REFUSED_BY_WORD_TOAST }, failureCauseByCommentEntry));
    toastError(COMMENTS_REFUSED_BY_WORD_TOAST);
};

type CommentWriteErrorReporter = (error: any, context?: any) => void;

interface CommentWriteErrorOfTheRun {
    error: unknown;
    context: unknown;
}

interface CommentWriteErrorsOfTheRun {
    collect: CommentWriteErrorReporter;
    sendOncePerDistinctError: (sendError?: CommentWriteErrorReporter) => void;
}

const distinctCommentWriteErrorKeyOf = (error: unknown, context: unknown): string =>
    `${String((context as { action?: unknown } | undefined)?.action)}:${error instanceof Error ? `${error.name}:${error.message}` : String(error)}`;

const commentWriteErrorsOfTheRun = (): CommentWriteErrorsOfTheRun => {
    const collected: CommentWriteErrorOfTheRun[] = [];
    return {
        collect: (error, context) => {
            collected.push({ error, context });
        },
        sendOncePerDistinctError: (sendError) => {
            const errorsByDistinctError = new Map<string, CommentWriteErrorOfTheRun[]>();
            for (const collectedError of collected) {
                const distinctErrorKey = distinctCommentWriteErrorKeyOf(collectedError.error, collectedError.context);
                errorsByDistinctError.set(distinctErrorKey, [...(errorsByDistinctError.get(distinctErrorKey) ?? []), collectedError]);
            }
            errorsByDistinctError.forEach((errorsAlike) => {
                const [firstError] = errorsAlike;
                sendError?.(firstError.error, { ...(firstError.context as object), commentCount: errorsAlike.length, comments: errorsAlike.map(({ context }) => context) });
            });
        },
    };
};

const commentsNotAddedToastOf = (failedCommentCount: number): string =>
    `${failedCommentCount} ${failedCommentCount === 1 ? "comment" : "comments"} could not be added; each card says why.`;

const writeCommentsUntilWordRefusesTwiceInARow = async (
    entries: BulkApplyCommentEntry[],
    failureCauseByCommentEntry: FailureCauseByCommentEntry,
    relocationByCommentEntry: RelocationByCommentEntry,
    sendError: CommentWriteErrorReporter | undefined,
    options?: BulkCommentWriteOptions,
): Promise<void> => {
    let processed = 0;
    let consecutiveCommentRefusals = 0;
    for (const commentEntry of entries) {
        const outcome = await applySingleCommentEntry(commentEntry, failureCauseByCommentEntry, relocationByCommentEntry, sendError);
        processed += 1;
        options?.onProgress?.({ processed, total: entries.length, inFlight: 1, mode: "individual" });
        consecutiveCommentRefusals = outcome === "refused_by_word" ? consecutiveCommentRefusals + 1 : 0;
        if (consecutiveCommentRefusals === CONSECUTIVE_COMMENT_REFUSALS_THAT_SKIP_THE_REST) {
            skipCommentsAfterWordRefusal(entries.slice(processed), failureCauseByCommentEntry);
            options?.onProgress?.({ processed: entries.length, total: entries.length, inFlight: 0, mode: "individual" });
            break;
        }
        if (outcome !== "settled") toastError(`We had an error applying the comment ${commentEntry.comment_text}. Skipping ...`);
    }
};

const writeEveryCommentOnItsOwn = async (
    entries: BulkApplyCommentEntry[],
    failureCauseByCommentEntry: FailureCauseByCommentEntry,
    relocationByCommentEntry: RelocationByCommentEntry,
    reportError: CommentWriteErrorReporter,
    options?: BulkCommentWriteOptions,
): Promise<void> => {
    let processed = 0;
    for (const commentEntry of entries) {
        await applySingleCommentEntry(commentEntry, failureCauseByCommentEntry, relocationByCommentEntry, reportError);
        processed += 1;
        options?.onProgress?.({ processed, total: entries.length, inFlight: 1, mode: "individual" });
    }
};

interface DedupedCommentEntries {
    entriesToWrite: BulkApplyCommentEntry[];
    firstEntryOfDuplicate: Map<BulkApplyCommentEntry, BulkApplyCommentEntry>;
}

const dedupeValidCommentEntries = (
    commentData: BulkApplyCommentEntry[],
    failureCauseByCommentEntry: FailureCauseByCommentEntry,
    sendError?: (error: any, context?: any) => void,
): DedupedCommentEntries => {
    const firstEntryByCommentKey = new Map<string, BulkApplyCommentEntry>();
    const firstEntryOfDuplicate = new Map<BulkApplyCommentEntry, BulkApplyCommentEntry>();
    const entries: BulkApplyCommentEntry[] = [];
    for (const commentEntry of commentData) {
        const { target_bookmark_names, comment_text } = commentEntry;
        const commentEntryIsMissingWritableFields = (): boolean => {
            const targetBookmarkNamesAreMissing = !target_bookmark_names;
            if (targetBookmarkNamesAreMissing) return true;
            const commentTextIsMissing = !comment_text;
            if (commentTextIsMissing) return true;
            return target_bookmark_names.length === 0;
        };
        /**
         * WHEN a bulk comment entry has no bookmark target or no comment text
         *   CONSEQUENCE
         *     Drop it as unwritable.
         */
        if (commentEntryIsMissingWritableFields()) {
            const failedRules = failCommentEntry(commentEntry, COMMENT_PLACE_NOT_FOUND, failureCauseByCommentEntry);
            sendError?.(new Error(UNWRITABLE_COMMENT_ENTRY_MESSAGE), {
                component: "applyBulkCommentData",
                action: "apply_comment",
                rules: failedRules,
                bookmarks: target_bookmark_names ?? [],
            });
            /**
             * WHEN the comment entry was dropped as incomplete
             *   CONSEQUENCE
             *     Skip it so only writable comments proceed to apply.
             */
            continue;
        }
        const commentKey = `${target_bookmark_names.join(',')}_${comment_text}`;
        const firstIdenticalEntry = firstEntryByCommentKey.get(commentKey);
        /**
         * WHEN the same bookmark set + comment text pair was already seen
         *   CONSEQUENCE
         *     Suppress the duplicate.
         */
        if (firstIdenticalEntry !== undefined) {
            firstEntryOfDuplicate.set(commentEntry, firstIdenticalEntry);
            continue;
        }
        firstEntryByCommentKey.set(commentKey, commentEntry);
        entries.push(commentEntry);
    }
    return { entriesToWrite: entries, firstEntryOfDuplicate };
};

const commentWriteResultOf = (
    commentData: BulkApplyCommentEntry[],
    failureCauseByCommentEntry: FailureCauseByCommentEntry,
    relocationByCommentEntry: RelocationByCommentEntry,
    firstEntryOfDuplicate: Map<BulkApplyCommentEntry, BulkApplyCommentEntry>,
): BulkCommentWriteResult => {
    const commentEntryFailures = commentData.flatMap((commentEntry): CommentEntryFailure[] => {
        const failureCause = failureCauseByCommentEntry.get(firstEntryOfDuplicate.get(commentEntry) ?? commentEntry);
        return failureCause === undefined ? [] : [{ commentEntry, ...failureCause }];
    });
    const commentEntryRelocations = commentData.flatMap((commentEntry): CommentEntryRelocation[] => {
        const relocation = relocationByCommentEntry.get(firstEntryOfDuplicate.get(commentEntry) ?? commentEntry);
        return relocation === undefined ? [] : [{ commentEntry, ...relocation }];
    });
    const failedEntriesInFailureOrder = [
        ...Array.from(failureCauseByCommentEntry.keys()),
        ...commentEntryFailures.map(({ commentEntry }) => commentEntry).filter((commentEntry) => firstEntryOfDuplicate.has(commentEntry)),
    ];
    return {
        failedRuleIds: Array.from(new Set(failedEntriesInFailureOrder.flatMap((commentEntry) => commentEntry.rule_uuids ?? []))),
        commentEntryFailures,
        commentEntryRelocations,
    };
};

export const applyBulkCommentData = async (commentData: BulkApplyCommentEntry[], sendError?: (error: any, context?: any) => void, options?: BulkCommentWriteOptions): Promise<BulkCommentWriteResult> => {
    const commentDataIsMissingOrEmpty = (): boolean => {
        const commentDataIsMissing = !commentData;
        if (commentDataIsMissing) return true;
        return commentData.length === 0;
    };
    /**
     * WHEN the caller supplied no comment entries
     *   CONSEQUENCE
     *     Return immediately.
     */
    if (commentDataIsMissingOrEmpty()) {
        return { failedRuleIds: [], commentEntryFailures: [], commentEntryRelocations: [] };
    }

    const triesEveryComment = options?.triesEveryComment === true;
    const errorsOfTheRun = commentWriteErrorsOfTheRun();
    const reportError: CommentWriteErrorReporter | undefined = triesEveryComment ? errorsOfTheRun.collect : sendError;
    const failureCauseByCommentEntry: FailureCauseByCommentEntry = new Map();
    const relocationByCommentEntry: RelocationByCommentEntry = new Map();
    const { entriesToWrite: entries, firstEntryOfDuplicate } = dedupeValidCommentEntries(commentData, failureCauseByCommentEntry, reportError);
    const finishedCommentWrites = (): BulkCommentWriteResult => {
        if (triesEveryComment) {
            errorsOfTheRun.sendOncePerDistinctError(sendError);
            if (failureCauseByCommentEntry.size > 0) toastError(commentsNotAddedToastOf(failureCauseByCommentEntry.size));
        }
        return commentWriteResultOf(commentData, failureCauseByCommentEntry, relocationByCommentEntry, triesEveryComment ? firstEntryOfDuplicate : new Map());
    };
    /**
     * WHEN validation and dedupe dropped every supplied comment entry
     *   CONSEQUENCE
     *     Return with the accumulated failed rule ids.
     */
    if (entries.length === 0) {
        return finishedCommentWrites();
    }

    let batched = false;
    /**
     * WHEN more than one comment entry survived filtering
     *   CONSEQUENCE
     *     Try the batched comment path first.
     */
    if (entries.length > 1) {
        try {
            options?.onProgress?.({ processed: 0, total: entries.length, inFlight: entries.length, mode: "batched" });
            await timedBulkApplySpan("comment_batch", () => withWordContextWrite(async (context) => {
                await withTemporaryTrackingMode(context, Word.ChangeTrackingMode.off, async () => {
                    const outcome = await applyCommentBatch(context, entries, relocationByCommentEntry, options?.bodyReadOfTheMerges ?? null);
                    outcome.missingBookmarkEntries.forEach((entry) =>
                        recordMissingCommentBookmarks(entry, failureCauseByCommentEntry, reportError));
                    const applied = entries.length - outcome.missingBookmarkEntries.length;
                    options?.onProgress?.({ processed: applied, total: entries.length, inFlight: applied, mode: "batched" });
                });
            }), { entries: entries.length });
            batched = true;
        } catch (error) {
            console.error(`Batch comment apply failed for ${entries.length} entries, retrying individually:`, error);
            reportError?.(error, {
                component: "applyBulkCommentData",
                action: "apply_comment_batch",
                batchSize: entries.length,
            });
        }
    }

    /**
     * WHEN batch mode was impossible or failed
     *   CONSEQUENCE
     *     Fall back to one-by-one comment application.
     */
    if (!batched) {
        if (triesEveryComment) {
            await timedBulkApplySpan("comment_individual_writes", () => reportCommentInsertRefusalsOncePerOutcome(() => writeEveryCommentOnItsOwn(entries, failureCauseByCommentEntry, relocationByCommentEntry, errorsOfTheRun.collect, options)), { entries: entries.length });
        } else {
            await timedBulkApplySpan("comment_individual_writes", () => writeCommentsUntilWordRefusesTwiceInARow(entries, failureCauseByCommentEntry, relocationByCommentEntry, sendError, options), { entries: entries.length });
        }
    }

    return finishedCommentWrites();
};

export const isDocumentWriteRestricted = (): Promise<boolean> => {
    return withWordContextRead(async (context) => {
        try {
            const doc = context.document as any;
            doc.load("protectionType");
            await context.sync();

            const protectionType = doc.protectionType;
            const restrictedTypes = ["AllowOnlyRevisions", "AllowOnlyComments", "AllowOnlyFormFields", "AllowOnlyReading"];
            return restrictedTypes.includes(protectionType);
        } catch (error) {
            /**
             * WHEN the protection check failed for a reason other than
             *   `PropertyNotLoaded`
             *   CONSEQUENCE
             *     Warn about the unexpected failure.
             *
             * WHY
             *   Some hosts simply do not expose `protectionType`; that case is expected.
             */
            if ((error as { code?: string })?.code !== "PropertyNotLoaded") {
                console.warn("Could not check document protection type:", error);
            }
            return false;
        }
    });
};

export const diagnoseDocumentError = (error: Error): Error => {
    if (!(error instanceof UserFacingError) && isWordBusyRefusal(error)) return new UserFacingError(WORD_BUSY_MESSAGE, error);
    /**
     * WHEN the error object carries a host error code
     *   CONSEQUENCE
     *     Reclassify it with Word-specific diagnosis.
     */
    if ("code" in error) {
        switch (error.code) {
            /**
             * WHEN the generic Word exception came from a document with empty URL
             *   CONSEQUENCE
             *     Diagnose it as an unsaved local document problem.
             */
            case Word.ErrorCodes.generalException:
                if (Office.context.document.url === "") {
                    /**
                     * WHEN the same generic Word exception is not explained by an empty URL
                     *   CONSEQUENCE
                     *     Map it to the broader apply-failure message.
                     */
                    return new UserFacingError("This document is not saved locally. Please save it manually before proceeding.", error);
                } else {
                    return new UserFacingError("Unable to apply these changes to the document.", error);
                }
            /**
             * WHEN the error has no host error code
             *   CONSEQUENCE
             *     Return it unchanged.
             */
            default:
                return error;
        }
    } else {

        return error;
    }
};

export const createNewDocumentFromHtml = async (htmlContent: string): Promise<void> => {
    return withWordContextWrite(async (context) => {
        const newDocument = context.application.createDocument();
        context.load(newDocument);
        await context.sync();

        newDocument.body.clear();
        newDocument.body.insertHtml(htmlContent, Word.InsertLocation.end);
        await context.sync();

        newDocument.open();
        await context.sync();
    });
};

export const downloadMessageAsDocx = async (messageContent: string): Promise<void> => {
    /**
     * WHEN running in Word for the web
     *   CONSEQUENCE
     *     Block message-to-docx download and throw a user-facing error.
     *
     * WHY
     *   Add-in code there cannot open the newly created document.
     */
    if (Office.context.platform === Office.PlatformType.OfficeOnline) {
        throw new UserFacingError(
            "Downloading this message as a Word document isn't supported in Word for the web. Open the document in Word for Windows or Mac and try again."
        );
    }
    try {
        const withImages = await inlineMermaidDiagrams(messageContent);
        const htmlContent = convertMarkdownToHtml(withImages);
        await createNewDocumentFromHtml(htmlContent);
    } catch (error) {
        console.error("Error creating document from message:", error);
        throw error;
    }
};
